// Agro Nazorat — landing page
// Pure React.createElement via JSX, no imports — `React` is global from /vendor.

const t = (k, fallback) => fallback;

const styles = {
  page: {
    minHeight: "100vh",
    fontFamily: "var(--font-ui)",
    color: "var(--ink)",
  },
  nav: {
    position: "sticky",
    top: 0,
    zIndex: 50,
    background: "color-mix(in oklab, var(--paper) 85%, transparent)",
    backdropFilter: "saturate(140%) blur(10px)",
    WebkitBackdropFilter: "saturate(140%) blur(10px)",
    borderBottom: "1px solid var(--rule)",
  },
  navInner: {
    maxWidth: 1240,
    margin: "0 auto",
    padding: "16px var(--mkt-pad)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 24,
  },
  brand: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    fontFamily: "var(--font-display)",
    fontSize: 22,
    letterSpacing: "-0.01em",
  },
  brandMark: {
    display: "inline-flex",
    width: 26,
    height: 26,
    borderRadius: 6,
    background: "var(--accent)",
    color: "var(--accent-fg)",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    fontWeight: 600,
    fontFamily: "var(--font-ui)",
  },
  navLinks: {
    display: "flex",
    alignItems: "center",
    gap: 28,
    fontSize: 14,
    color: "var(--ink-2)",
  },
  btnSolid: {
    background: "var(--ink)",
    color: "var(--paper)",
    padding: "10px 18px",
    borderRadius: 999,
    fontSize: 14,
    fontWeight: 500,
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
  },
  btnGhost: {
    color: "var(--ink-2)",
    padding: "10px 14px",
    borderRadius: 999,
    fontSize: 14,
  },

  hero: {
    maxWidth: 1240,
    margin: "0 auto",
    padding: "clamp(64px, 10vh, 140px) var(--mkt-pad) clamp(80px, 12vh, 160px)",
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.1fr) minmax(0, 0.9fr)",
    gap: "max(32px, 6vw)",
    alignItems: "center",
  },
  heroEyebrow: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    fontSize: 12,
    fontFamily: "var(--font-mono)",
    color: "var(--ink-3)",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
  },
  dot: {
    display: "inline-block",
    width: 6,
    height: 6,
    borderRadius: 999,
    background: "var(--accent)",
  },
  h1: {
    fontFamily: "var(--font-display)",
    fontWeight: 400,
    fontSize: "clamp(34px, 6.5vw, 76px)",
    lineHeight: 1.05,
    letterSpacing: "-0.022em",
    margin: "20px 0 24px",
  },
  h1Em: {
    fontStyle: "italic",
    color: "var(--accent)",
  },
  lede: {
    fontSize: 19,
    lineHeight: 1.55,
    color: "var(--ink-2)",
    maxWidth: 560,
    marginBottom: 32,
  },
  heroCtaRow: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
  },
  heroCta: {
    background: "var(--ink)",
    color: "var(--paper)",
    padding: "14px 22px",
    borderRadius: 999,
    fontSize: 15,
    fontWeight: 500,
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
  },
  heroCta2: {
    color: "var(--ink-2)",
    padding: "14px 18px",
    borderRadius: 999,
    fontSize: 15,
    border: "1px solid var(--rule)",
    background: "var(--paper-2)",
  },

  // The hero "preview window" — a stylised KPI card to the right.
  previewWrap: {
    position: "relative",
    minWidth: 0,
  },
  previewWindow: {
    background: "var(--paper-2)",
    border: "1px solid var(--rule)",
    borderRadius: 14,
    boxShadow: "0 30px 80px -40px rgba(0,0,0,0.25)",
    overflow: "hidden",
    maxWidth: "100%",
  },
  previewChrome: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 14px",
    borderBottom: "1px solid var(--rule)",
    background: "var(--paper-3)",
  },
  dotSm: {
    width: 10,
    height: 10,
    borderRadius: 999,
  },
  previewBody: {
    padding: "22px 24px 24px",
  },
  previewLabel: {
    fontSize: 11,
    fontFamily: "var(--font-mono)",
    color: "var(--ink-3)",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
  },
  previewName: {
    fontFamily: "var(--font-display)",
    fontSize: 24,
    margin: "6px 0 4px",
  },
  previewMeta: {
    fontSize: 13,
    color: "var(--ink-3)",
    marginBottom: 18,
    lineHeight: 1.45,
  },
  scoreRow: {
    display: "flex",
    alignItems: "baseline",
    gap: 12,
    marginBottom: 14,
  },
  scoreNum: {
    fontFamily: "var(--font-display)",
    fontSize: 56,
    color: "var(--accent)",
    lineHeight: 1,
  },
  scoreOf: {
    fontSize: 14,
    color: "var(--ink-3)",
  },
  scoreBar: {
    height: 6,
    borderRadius: 999,
    background: "var(--paper-3)",
    overflow: "hidden",
    marginBottom: 18,
  },
  scoreBarFill: {
    height: "100%",
    background: "var(--accent)",
    borderRadius: 999,
  },
  componentsList: {
    display: "grid",
    gap: 8,
  },
  componentRow: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto auto",
    gap: 12,
    fontSize: 13,
    padding: "10px 0",
    borderTop: "1px solid var(--rule)",
    alignItems: "center",
  },
  componentName: {
    color: "var(--ink-2)",
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  componentWeight: {
    color: "var(--ink-3)",
    fontFamily: "var(--font-mono)",
    fontSize: 11,
  },
  componentScore: {
    color: "var(--ink)",
    fontFamily: "var(--font-mono)",
    fontWeight: 500,
  },

  // Section
  section: {
    maxWidth: 1240,
    margin: "0 auto",
    padding: "clamp(56px, 9vh, 120px) var(--mkt-pad)",
  },
  sectionEyebrow: {
    fontSize: 12,
    fontFamily: "var(--font-mono)",
    color: "var(--accent)",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    marginBottom: 14,
  },
  sectionH2: {
    fontFamily: "var(--font-display)",
    fontWeight: 400,
    fontSize: "clamp(28px, 4vw, 48px)",
    lineHeight: 1.1,
    letterSpacing: "-0.02em",
    margin: "0 0 16px",
    maxWidth: 720,
  },
  sectionLede: {
    fontSize: 17,
    lineHeight: 1.55,
    color: "var(--ink-2)",
    maxWidth: 640,
    marginBottom: 48,
  },

  // KPI weight cards (4-up)
  kpiGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 16,
  },
  kpiCard: {
    border: "1px solid var(--rule)",
    background: "var(--paper-2)",
    borderRadius: 14,
    padding: "22px 22px 24px",
    cursor: "default",
  },
  kpiPct: {
    fontFamily: "var(--font-display)",
    fontSize: 52,
    color: "var(--accent)",
    lineHeight: 1,
    letterSpacing: "-0.02em",
  },
  kpiName: {
    fontSize: 15,
    fontWeight: 500,
    marginTop: 14,
    marginBottom: 6,
  },
  kpiDesc: {
    fontSize: 13.5,
    color: "var(--ink-3)",
    lineHeight: 1.5,
  },

  // Category cards (3-up)
  catGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 18,
  },
  catCard: {
    border: "1px solid var(--rule)",
    background: "var(--paper-2)",
    borderRadius: 16,
    padding: "26px 24px 28px",
    cursor: "default",
  },
  catGlyph: {
    fontSize: 26,
    width: 44,
    height: 44,
    borderRadius: 10,
    background: "var(--accent-soft)",
    color: "var(--accent)",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 18,
    fontFamily: "var(--font-display)",
  },
  catName: {
    fontFamily: "var(--font-display)",
    fontSize: 24,
    marginBottom: 6,
    letterSpacing: "-0.01em",
  },
  catDesc: {
    fontSize: 14,
    color: "var(--ink-2)",
    lineHeight: 1.55,
    marginBottom: 16,
  },
  catList: {
    listStyle: "none",
    padding: 0,
    margin: 0,
    display: "grid",
    gap: 6,
  },
  catItem: {
    fontSize: 13,
    color: "var(--ink-3)",
    fontFamily: "var(--font-mono)",
    paddingLeft: 14,
    position: "relative",
  },

  // Integration ribbon
  intGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 1,
    background: "var(--rule)",
    border: "1px solid var(--rule)",
    borderRadius: 14,
    overflow: "hidden",
  },
  intCell: {
    background: "var(--paper-2)",
    padding: "22px 20px",
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  intCellName: {
    fontSize: 14,
    fontWeight: 500,
    color: "var(--ink)",
  },
  intCellWhat: {
    fontSize: 12,
    color: "var(--ink-3)",
    fontFamily: "var(--font-mono)",
  },

  // Rank section (Reyting dashboard preview)
  rankWrap: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 0.95fr) minmax(0, 1.05fr)",
    gap: "max(32px, 5vw)",
    alignItems: "center",
  },
  rankCopy: {
    minWidth: 0,
  },
  rankWindow: {
    minWidth: 0,
    background: "var(--paper-2)",
    border: "1px solid var(--rule)",
    borderRadius: 14,
    boxShadow: "0 30px 80px -40px rgba(0,0,0,0.25)",
    overflow: "hidden",
    maxWidth: "100%",
  },
  rankTabs: {
    display: "flex",
    gap: 0,
    borderBottom: "1px solid var(--rule)",
    background: "var(--paper-3)",
    padding: "0 14px",
  },
  rankTab: {
    padding: "12px 14px",
    fontSize: 12,
    fontFamily: "var(--font-mono)",
    color: "var(--ink-3)",
    borderBottom: "2px solid transparent",
    letterSpacing: "0.04em",
  },
  rankTabActive: {
    color: "var(--ink)",
    borderBottomColor: "var(--accent)",
  },
  rankBody: {
    padding: "20px 22px 24px",
  },
  rankFilterRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 16,
    flexWrap: "wrap",
  },
  rankRegion: {
    fontSize: 12,
    color: "var(--ink-3)",
    fontFamily: "var(--font-mono)",
    letterSpacing: "0.04em",
    textTransform: "uppercase",
  },
  rankSortBtn: {
    fontSize: 11.5,
    color: "var(--ink-3)",
    fontFamily: "var(--font-mono)",
    padding: "4px 10px",
    border: "1px solid var(--rule)",
    borderRadius: 999,
    background: "var(--paper)",
  },
  rankList: {
    display: "grid",
    gap: 2,
  },
  rankRow: {
    display: "grid",
    gridTemplateColumns: "26px minmax(0, 1fr) auto auto",
    gap: 12,
    padding: "12px 0",
    alignItems: "center",
    borderTop: "1px solid var(--rule)",
  },
  rankNum: {
    fontFamily: "var(--font-mono)",
    fontSize: 12,
    color: "var(--ink-3)",
  },
  rankBody2: {
    minWidth: 0,
  },
  rankNameLine: {
    fontSize: 14,
    fontWeight: 500,
    color: "var(--ink)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  rankMetaLine: {
    fontSize: 11.5,
    color: "var(--ink-3)",
    fontFamily: "var(--font-mono)",
    marginTop: 2,
  },
  rankMiniBar: {
    width: 100,
    height: 5,
    borderRadius: 999,
    background: "var(--paper-3)",
    overflow: "hidden",
  },
  rankMiniBarFill: {
    height: "100%",
    borderRadius: 999,
  },
  rankScore: {
    fontFamily: "var(--font-mono)",
    fontSize: 13,
    fontWeight: 500,
    color: "var(--ink)",
    minWidth: 36,
    textAlign: "right",
  },

  // Flow section
  flowGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 60px 1fr 60px 1fr",
    gap: 0,
    alignItems: "stretch",
    border: "1px solid var(--rule)",
    borderRadius: 16,
    background: "var(--paper-2)",
    overflow: "hidden",
  },
  flowStage: {
    padding: "26px 26px 28px",
    minWidth: 0,
    borderRight: "1px solid var(--rule)",
  },
  flowConnector: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "var(--ink-3)",
    fontSize: 22,
    fontFamily: "var(--font-mono)",
  },
  flowStageNum: {
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    color: "var(--accent)",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    marginBottom: 10,
  },
  flowStageName: {
    fontFamily: "var(--font-display)",
    fontSize: 22,
    marginBottom: 12,
    letterSpacing: "-0.01em",
  },
  flowStageDesc: {
    fontSize: 13,
    color: "var(--ink-2)",
    lineHeight: 1.55,
    marginBottom: 18,
  },
  flowChips: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
  },
  flowChip: {
    padding: "5px 10px",
    background: "var(--paper-3)",
    border: "1px solid var(--rule)",
    borderRadius: 999,
    fontSize: 11.5,
    color: "var(--ink-2)",
    fontFamily: "var(--font-mono)",
    whiteSpace: "nowrap",
  },
  flowFormula: {
    background: "var(--paper-3)",
    border: "1px solid var(--rule)",
    borderRadius: 8,
    padding: "12px 14px",
    fontFamily: "var(--font-mono)",
    fontSize: 12,
    color: "var(--ink-2)",
    lineHeight: 1.6,
    overflowX: "auto",
  },
  flowFormulaEm: {
    color: "var(--accent)",
    fontWeight: 500,
  },
  flowTiers: {
    display: "grid",
    gap: 6,
  },
  flowTier: {
    display: "grid",
    gridTemplateColumns: "auto 1fr auto",
    gap: 10,
    alignItems: "center",
    padding: "8px 0",
    fontSize: 13,
    borderTop: "1px solid var(--rule)",
  },
  flowTierBand: {
    width: 4,
    height: 22,
    borderRadius: 2,
  },
  flowTierName: {
    color: "var(--ink-2)",
  },
  flowTierRange: {
    color: "var(--ink-3)",
    fontFamily: "var(--font-mono)",
    fontSize: 11,
  },

  // Misol (worked example)
  misolWrap: {
    border: "1px solid var(--rule)",
    background: "var(--paper-2)",
    borderRadius: 16,
    overflow: "hidden",
  },
  misolHeader: {
    padding: "20px 26px",
    borderBottom: "1px solid var(--rule)",
    background: "var(--paper-3)",
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
  },
  misolHeaderName: {
    fontFamily: "var(--font-display)",
    fontSize: 20,
    letterSpacing: "-0.01em",
  },
  misolHeaderMeta: {
    fontSize: 12,
    color: "var(--ink-3)",
    fontFamily: "var(--font-mono)",
  },
  misolRow: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.1fr) minmax(0, 1.4fr) minmax(0, 0.7fr) auto",
    gap: 24,
    padding: "22px 26px",
    borderBottom: "1px solid var(--rule)",
    alignItems: "center",
  },
  misolRowLast: {
    borderBottom: "none",
  },
  misolCompName: {
    fontSize: 14,
    fontWeight: 500,
    color: "var(--ink)",
  },
  misolWeight: {
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    color: "var(--ink-3)",
    marginTop: 2,
  },
  misolInputs: {
    display: "grid",
    gap: 4,
    fontSize: 12.5,
    color: "var(--ink-2)",
    fontFamily: "var(--font-mono)",
    minWidth: 0,
  },
  misolInputRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
  },
  misolInputKey: {
    color: "var(--ink-3)",
  },
  misolInputVal: {
    color: "var(--ink)",
  },
  misolFormula: {
    fontFamily: "var(--font-mono)",
    fontSize: 12,
    color: "var(--ink-3)",
    lineHeight: 1.55,
    minWidth: 0,
  },
  misolScore: {
    fontFamily: "var(--font-display)",
    fontSize: 28,
    color: "var(--accent)",
    textAlign: "right",
    minWidth: 90,
    letterSpacing: "-0.01em",
  },
  misolScoreOf: {
    fontSize: 13,
    color: "var(--ink-3)",
    fontFamily: "var(--font-mono)",
  },
  misolFooter: {
    padding: "26px 26px 28px",
    background: "var(--paper-3)",
    borderTop: "1px solid var(--rule)",
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto",
    alignItems: "center",
    gap: 24,
  },
  misolFinalFormula: {
    fontFamily: "var(--font-mono)",
    fontSize: 13,
    color: "var(--ink-2)",
    lineHeight: 1.55,
  },
  misolFinalScore: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: 4,
  },
  misolFinalNum: {
    fontFamily: "var(--font-display)",
    fontSize: 56,
    color: "var(--accent)",
    lineHeight: 1,
  },
  misolFinalTier: {
    fontSize: 12,
    fontFamily: "var(--font-mono)",
    color: "var(--ink-2)",
    padding: "4px 10px",
    borderRadius: 999,
    background: "color-mix(in oklab, oklch(0.78 0.14 95) 25%, var(--paper))",
    border: "1px solid color-mix(in oklab, oklch(0.78 0.14 95) 50%, var(--rule))",
  },

  // Try-it (Sinab ko'ring) calculator
  tryWrap: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 0.85fr)",
    gap: 0,
    border: "1px solid var(--rule)",
    borderRadius: 16,
    background: "var(--paper-2)",
    overflow: "hidden",
  },
  tryControls: {
    padding: "26px 28px 28px",
    minWidth: 0,
    borderRight: "1px solid var(--rule)",
  },
  tryRow: {
    marginBottom: 22,
    minWidth: 0,
  },
  tryRowLast: {
    marginBottom: 0,
  },
  tryLabelRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    marginBottom: 8,
    gap: 12,
  },
  tryLabel: {
    fontSize: 13.5,
    color: "var(--ink-2)",
    fontWeight: 500,
  },
  tryValue: {
    fontFamily: "var(--font-mono)",
    fontSize: 13,
    color: "var(--accent)",
    fontWeight: 600,
  },
  tryValueOf: {
    color: "var(--ink-3)",
    fontWeight: 400,
    marginLeft: 4,
  },
  trySlider: {
    width: "100%",
    height: 4,
    appearance: "none",
    WebkitAppearance: "none",
    background: "var(--paper-3)",
    borderRadius: 999,
    outline: "none",
    cursor: "pointer",
  },
  tryHint: {
    fontSize: 11.5,
    color: "var(--ink-3)",
    fontFamily: "var(--font-mono)",
    marginTop: 8,
  },
  tryResult: {
    padding: "26px 28px 28px",
    minWidth: 0,
    background: "var(--paper-3)",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  },
  tryResultLabel: {
    fontSize: 11,
    fontFamily: "var(--font-mono)",
    color: "var(--ink-3)",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    marginBottom: 6,
  },
  tryResultScore: {
    fontFamily: "var(--font-display)",
    fontSize: 88,
    lineHeight: 1,
    letterSpacing: "-0.025em",
  },
  tryResultOf: {
    fontSize: 14,
    color: "var(--ink-3)",
    fontFamily: "var(--font-mono)",
    marginTop: 6,
  },
  tryTierBand: {
    height: 8,
    borderRadius: 999,
    background: "linear-gradient(90deg, oklch(0.62 0.18 25) 0%, oklch(0.62 0.18 25) 55%, oklch(0.7 0.16 60) 55%, oklch(0.7 0.16 60) 70%, oklch(0.78 0.14 95) 70%, oklch(0.78 0.14 95) 85%, oklch(0.62 0.16 152) 85%, oklch(0.62 0.16 152) 100%)",
    position: "relative",
    marginTop: 28,
    marginBottom: 10,
  },
  tryTierMarker: {
    position: "absolute",
    top: -6,
    width: 4,
    height: 20,
    background: "var(--ink)",
    borderRadius: 2,
    transition: "left 0.18s cubic-bezier(.2,.7,.4,1)",
  },
  tryTierLegend: {
    display: "grid",
    gridTemplateColumns: "55fr 15fr 15fr 15fr",
    fontSize: 10.5,
    fontFamily: "var(--font-mono)",
    color: "var(--ink-3)",
    letterSpacing: "0.04em",
  },
  tryTierLegendCell: {
    textAlign: "center",
    paddingTop: 2,
  },
  tryTierName: {
    fontFamily: "var(--font-mono)",
    fontSize: 12,
    padding: "6px 14px",
    borderRadius: 999,
    display: "inline-block",
    marginTop: 16,
    letterSpacing: "0.04em",
  },

  // Outcomes (Qarorlar)
  outcomeGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 16,
  },
  outcomeCard: {
    border: "1px solid var(--rule)",
    background: "var(--paper-2)",
    borderRadius: 14,
    padding: "0 0 24px",
    overflow: "hidden",
    cursor: "default",
    minWidth: 0,
  },
  outcomeBand: {
    height: 4,
  },
  outcomeBody: {
    padding: "22px 22px 0",
  },
  outcomeTier: {
    fontSize: 11,
    fontFamily: "var(--font-mono)",
    color: "var(--ink-3)",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    marginBottom: 8,
  },
  outcomeName: {
    fontFamily: "var(--font-display)",
    fontSize: 22,
    marginBottom: 12,
    letterSpacing: "-0.01em",
    lineHeight: 1.15,
  },
  outcomeDesc: {
    fontSize: 13.5,
    color: "var(--ink-2)",
    lineHeight: 1.55,
    marginBottom: 16,
  },
  outcomeRange: {
    display: "inline-flex",
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: 11,
    fontFamily: "var(--font-mono)",
    background: "var(--paper-3)",
    color: "var(--ink-3)",
    letterSpacing: "0.04em",
  },

  // Roadmap
  roadGrid: {
    display: "grid",
    gridTemplateColumns: "120px 1fr",
    rowGap: 8,
    columnGap: 24,
  },
  roadPhaseLabel: {
    fontFamily: "var(--font-mono)",
    fontSize: 12,
    color: "var(--ink-3)",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    paddingTop: 18,
  },
  roadCard: {
    border: "1px solid var(--rule)",
    background: "var(--paper-2)",
    borderRadius: 12,
    padding: "16px 20px",
  },
  roadName: {
    fontSize: 16,
    fontWeight: 500,
    marginBottom: 4,
  },
  roadItems: {
    fontSize: 13.5,
    color: "var(--ink-2)",
    lineHeight: 1.55,
  },

  // FAQ
  faqList: {
    border: "1px solid var(--rule)",
    borderRadius: 16,
    background: "var(--paper-2)",
    overflow: "hidden",
  },
  faqItem: {
    borderBottom: "1px solid var(--rule)",
  },
  faqItemLast: {
    borderBottom: "none",
  },
  faqSummary: {
    listStyle: "none",
    cursor: "pointer",
    padding: "22px 26px",
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto",
    gap: 16,
    alignItems: "center",
    fontFamily: "var(--font-display)",
    fontSize: 19,
    lineHeight: 1.3,
    letterSpacing: "-0.005em",
    color: "var(--ink)",
    transition: "background 0.14s ease",
  },
  faqMark: {
    fontFamily: "var(--font-mono)",
    fontSize: 14,
    color: "var(--ink-3)",
    width: 14,
    textAlign: "center",
    transition: "transform 0.18s cubic-bezier(.2,.7,.4,1)",
  },
  faqAnswer: {
    padding: "0 26px 24px",
    fontSize: 14.5,
    color: "var(--ink-2)",
    lineHeight: 1.6,
    maxWidth: 740,
  },

  // Footer
  footer: {
    borderTop: "1px solid var(--rule)",
    marginTop: 80,
    background: "var(--paper-2)",
  },
  footerInner: {
    maxWidth: 1240,
    margin: "0 auto",
    padding: "56px var(--mkt-pad) 0",
  },
  footerGrid: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.4fr) repeat(3, minmax(0, 1fr))",
    gap: "max(32px, 4vw)",
    paddingBottom: 48,
  },
  footerBrandCol: {
    minWidth: 0,
  },
  footerBrandRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    fontFamily: "var(--font-display)",
    fontSize: 22,
    letterSpacing: "-0.01em",
    marginBottom: 12,
  },
  footerTagline: {
    fontSize: 13.5,
    color: "var(--ink-2)",
    lineHeight: 1.55,
    marginBottom: 22,
    maxWidth: 320,
  },
  langSwitch: {
    display: "inline-flex",
    border: "1px solid var(--rule)",
    borderRadius: 999,
    padding: 3,
    background: "var(--paper)",
  },
  langBtn: {
    padding: "5px 11px",
    fontSize: 11.5,
    fontFamily: "var(--font-mono)",
    color: "var(--ink-3)",
    borderRadius: 999,
    letterSpacing: "0.04em",
  },
  langBtnActive: {
    background: "var(--ink)",
    color: "var(--paper)",
  },
  footerCol: {
    minWidth: 0,
  },
  footerColTitle: {
    fontSize: 11,
    fontFamily: "var(--font-mono)",
    color: "var(--ink-3)",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    marginBottom: 16,
  },
  footerLinks: {
    listStyle: "none",
    padding: 0,
    margin: 0,
    display: "grid",
    gap: 10,
  },
  footerLink: {
    fontSize: 14,
    color: "var(--ink-2)",
    cursor: "pointer",
  },
  footerBar: {
    borderTop: "1px solid var(--rule)",
    padding: "20px 0",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 16,
    fontSize: 12,
    color: "var(--ink-3)",
    fontFamily: "var(--font-mono)",
    flexWrap: "wrap",
  },

  // Mezon (horticulture scoring criteria)
  mezonGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 18,
  },
  mezonCard: {
    border: "1px solid var(--rule)",
    background: "var(--paper-2)",
    borderRadius: 16,
    padding: "24px 24px 26px",
  },
  mezonHeader: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 16,
    paddingBottom: 14,
    borderBottom: "1px solid var(--rule)",
  },
  mezonTitle: {
    fontFamily: "var(--font-display)",
    fontSize: 20,
    letterSpacing: "-0.01em",
  },
  mezonTotal: {
    fontFamily: "var(--font-mono)",
    fontSize: 13,
    color: "var(--accent)",
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  mezonItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    gap: 12,
    padding: "7px 0",
    borderBottom: "1px dashed color-mix(in oklab, var(--rule) 60%, transparent)",
  },
  mezonItemLast: {
    borderBottom: "none",
  },
  mezonItemName: {
    fontSize: 13.5,
    color: "var(--ink-2)",
    lineHeight: 1.4,
  },
  mezonItemScore: {
    fontFamily: "var(--font-mono)",
    fontSize: 12.5,
    color: "var(--ink-3)",
    whiteSpace: "nowrap",
  },

  // Wizard
  wizardTab: {
    padding: "10px 18px",
    borderRadius: 999,
    border: "1px solid var(--rule)",
    background: "var(--paper-2)",
    fontSize: 14,
    fontWeight: 500,
    color: "var(--ink-2)",
    cursor: "pointer",
    transition: "background-color 0.14s ease, color 0.14s ease, border-color 0.14s ease",
  },
  wizardTabActive: {
    background: "var(--ink)",
    color: "var(--paper)",
    borderColor: "var(--ink)",
  },
  wizardGrid: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr)",
    gap: 18,
    maxWidth: 800,
  },
  wizardCard: {
    border: "1px solid var(--rule)",
    background: "var(--paper-2)",
    borderRadius: 16,
    padding: "22px 24px 24px",
  },
  wizardCatHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    marginBottom: 16,
    paddingBottom: 12,
    borderBottom: "1px solid var(--rule)",
  },
  wizardCatName: {
    fontFamily: "var(--font-display)",
    fontSize: 18,
    letterSpacing: "-0.01em",
  },
  wizardCatMax: {
    fontFamily: "var(--font-mono)",
    fontSize: 12,
    color: "var(--accent)",
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  wizardQuestion: {
    marginBottom: 18,
    paddingBottom: 18,
    borderBottom: "1px dashed color-mix(in oklab, var(--rule) 60%, transparent)",
  },
  wizardQText: {
    fontSize: 13.5,
    color: "var(--ink-2)",
    lineHeight: 1.4,
  },
  wizardOption: {
    padding: "10px 16px",
    borderRadius: 10,
    border: "1px solid var(--rule)",
    background: "var(--paper)",
    fontSize: 13.5,
    color: "var(--ink-2)",
    cursor: "pointer",
    fontWeight: 500,
    display: "block",
    width: "100%",
    textAlign: "left",
    transition: "background-color 0.14s ease, color 0.14s ease, border-color 0.14s ease, transform 0.08s ease",
  },
  wizardOptionActive: {
    background: "var(--accent)",
    color: "var(--accent-fg)",
    borderColor: "var(--accent)",
  },
  wizardResult: {
    border: "1px solid var(--rule)",
    background: "var(--paper-2)",
    borderRadius: 16,
    padding: "28px 28px 32px",
  },
  wizardResultScore: {
    fontFamily: "var(--font-display)",
    fontSize: 56,
    lineHeight: 1,
  },

  // Platform section (Dashboard / Registry / Profile)
  platformGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 18,
  },
  platformCard: {
    border: "1px solid var(--rule)",
    background: "var(--paper-2)",
    borderRadius: 14,
    overflow: "hidden",
    minWidth: 0,
  },
  platformChrome: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 14px",
    borderBottom: "1px solid var(--rule)",
    background: "var(--paper-3)",
  },
  platformBody: {
    padding: "18px 18px 20px",
  },
  platformTileGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: 10,
    marginBottom: 14,
  },
  platformTile: {
    background: "var(--paper-3)",
    border: "1px solid var(--rule)",
    borderRadius: 10,
    padding: "12px 14px",
  },
  platformTileLabel: {
    fontSize: 11,
    fontFamily: "var(--font-mono)",
    color: "var(--ink-3)",
    letterSpacing: "0.04em",
    marginBottom: 4,
  },
  platformTileValue: {
    fontFamily: "var(--font-display)",
    fontSize: 22,
    color: "var(--accent)",
    lineHeight: 1,
  },
  platformTable: {
    display: "grid",
    gap: 1,
    background: "var(--rule)",
    borderRadius: 10,
    overflow: "hidden",
    border: "1px solid var(--rule)",
  },
  platformTableRow: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto auto",
    gap: 10,
    padding: "8px 12px",
    background: "var(--paper)",
    alignItems: "center",
    fontSize: 12,
  },
  platformTableHeader: {
    background: "var(--paper-3)",
    fontFamily: "var(--font-mono)",
    fontSize: 10,
    color: "var(--ink-3)",
    letterSpacing: "0.04em",
    textTransform: "uppercase",
  },
  platformMiniBar: {
    height: 4,
    borderRadius: 999,
    background: "var(--paper-3)",
    overflow: "hidden",
    marginTop: 10,
  },
  platformMiniBarFill: {
    height: "100%",
    borderRadius: 999,
    background: "var(--accent)",
  },
  platformAlert: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 10px",
    borderRadius: 8,
    background: "color-mix(in oklab, oklch(0.62 0.16 25) 8%, var(--paper))",
    border: "1px solid color-mix(in oklab, oklch(0.62 0.16 25) 20%, var(--rule))",
    fontSize: 12,
    color: "var(--ink-2)",
    marginTop: 10,
  },

  // Monitoring section
  monGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 18,
  },
  monCard: {
    border: "1px solid var(--rule)",
    background: "var(--paper-2)",
    borderRadius: 14,
    padding: "22px 22px 24px",
    minWidth: 0,
  },
  monHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  monTitle: {
    fontFamily: "var(--font-display)",
    fontSize: 20,
    letterSpacing: "-0.01em",
  },
  monStatus: {
    fontSize: 11,
    fontFamily: "var(--font-mono)",
    padding: "4px 10px",
    borderRadius: 999,
    letterSpacing: "0.04em",
  },
  monValueRow: {
    display: "flex",
    alignItems: "baseline",
    gap: 10,
    marginBottom: 6,
  },
  monValue: {
    fontFamily: "var(--font-display)",
    fontSize: 36,
    lineHeight: 1,
    letterSpacing: "-0.02em",
  },
  monDelta: {
    fontFamily: "var(--font-mono)",
    fontSize: 13,
    fontWeight: 500,
  },
  monMeta: {
    fontSize: 12,
    color: "var(--ink-3)",
    marginBottom: 16,
  },
  monBarTrack: {
    height: 8,
    borderRadius: 999,
    background: "var(--paper-3)",
    overflow: "hidden",
    marginBottom: 6,
  },
  monBarFill: {
    height: "100%",
    borderRadius: 999,
  },
  monBarLabels: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: 11,
    fontFamily: "var(--font-mono)",
    color: "var(--ink-3)",
  },
  monSpark: {
    display: "flex",
    alignItems: "flex-end",
    gap: 3,
    height: 48,
    marginTop: 14,
  },
  monSparkBar: {
    flex: 1,
    borderRadius: "2px 2px 0 0",
    background: "var(--accent)",
    opacity: 0.35,
    minWidth: 4,
  },

  // Reports section
  reportsWrap: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 280px) minmax(0, 1fr)",
    gap: 0,
    border: "1px solid var(--rule)",
    borderRadius: 16,
    background: "var(--paper-2)",
    overflow: "hidden",
  },
  reportsList: {
    borderRight: "1px solid var(--rule)",
    background: "var(--paper-3)",
    padding: "16px 14px",
    display: "grid",
    gap: 4,
    alignContent: "start",
  },
  reportsListItem: {
    padding: "10px 12px",
    borderRadius: 10,
    fontSize: 13,
    fontWeight: 500,
    color: "var(--ink-2)",
    cursor: "pointer",
    border: "1px solid transparent",
    transition: "background 0.14s ease",
  },
  reportsListItemActive: {
    background: "var(--paper)",
    borderColor: "var(--rule)",
    color: "var(--ink)",
  },
  reportsPreview: {
    padding: "22px 24px 24px",
    minWidth: 0,
  },
  reportsPreviewTitle: {
    fontFamily: "var(--font-display)",
    fontSize: 18,
    marginBottom: 14,
  },
  reportsTable: {
    display: "grid",
    gap: 1,
    background: "var(--rule)",
    borderRadius: 10,
    overflow: "hidden",
    border: "1px solid var(--rule)",
    marginTop: 12,
  },
  reportsTableRow: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto auto",
    gap: 12,
    padding: "8px 12px",
    background: "var(--paper)",
    alignItems: "center",
    fontSize: 12,
  },
  reportsChartBars: {
    display: "flex",
    alignItems: "flex-end",
    gap: 8,
    height: 120,
    marginTop: 18,
    padding: "0 8px",
  },
  reportsChartBar: {
    flex: 1,
    borderRadius: "4px 4px 0 0",
    background: "var(--accent)",
    opacity: 0.5,
    minWidth: 16,
    position: "relative",
  },
  reportsChartLabel: {
    position: "absolute",
    bottom: -18,
    left: "50%",
    transform: "translateX(-50%)",
    fontSize: 10,
    fontFamily: "var(--font-mono)",
    color: "var(--ink-3)",
    whiteSpace: "nowrap",
  },

  // Risk section
  riskWrap: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 380px)",
    gap: "max(24px, 4vw)",
    alignItems: "start",
  },
  riskList: {
    display: "grid",
    gap: 10,
  },
  riskItem: {
    border: "1px solid var(--rule)",
    background: "var(--paper-2)",
    borderRadius: 12,
    padding: "16px 18px",
  },
  riskItemHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 6,
  },
  riskItemName: {
    fontSize: 14,
    fontWeight: 500,
  },
  riskBadge: {
    fontSize: 10,
    fontFamily: "var(--font-mono)",
    padding: "3px 8px",
    borderRadius: 999,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
  },
  riskItemDesc: {
    fontSize: 13,
    color: "var(--ink-2)",
    lineHeight: 1.5,
  },
  riskAiCard: {
    border: "1px solid var(--rule)",
    background: "var(--paper-2)",
    borderRadius: 14,
    padding: "24px 24px 26px",
    position: "sticky",
    top: 80,
  },
  riskAiBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: 11,
    fontFamily: "var(--font-mono)",
    color: "var(--accent)",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    marginBottom: 14,
  },
  riskAiTitle: {
    fontFamily: "var(--font-display)",
    fontSize: 22,
    marginBottom: 10,
  },
  riskAiDesc: {
    fontSize: 14,
    color: "var(--ink-2)",
    lineHeight: 1.6,
    marginBottom: 16,
  },
  riskAiRec: {
    background: "var(--paper-3)",
    border: "1px solid var(--rule)",
    borderRadius: 10,
    padding: "12px 14px",
    fontSize: 13,
    color: "var(--ink-2)",
    lineHeight: 1.5,
  },

  // Admin section
  adminWrap: {
    border: "1px solid var(--rule)",
    background: "var(--paper-2)",
    borderRadius: 16,
    overflow: "hidden",
  },
  adminTabs: {
    display: "flex",
    gap: 0,
    borderBottom: "1px solid var(--rule)",
    background: "var(--paper-3)",
    padding: "0 14px",
  },
  adminTab: {
    padding: "12px 16px",
    fontSize: 13,
    fontWeight: 500,
    color: "var(--ink-3)",
    borderBottom: "2px solid transparent",
    cursor: "pointer",
    transition: "color 0.14s ease",
  },
  adminTabActive: {
    color: "var(--ink)",
    borderBottomColor: "var(--accent)",
  },
  adminBody: {
    padding: "22px 24px 24px",
  },
  adminTable: {
    display: "grid",
    gap: 1,
    background: "var(--rule)",
    borderRadius: 10,
    overflow: "hidden",
    border: "1px solid var(--rule)",
  },
  adminTableRow: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) auto",
    gap: 12,
    padding: "9px 12px",
    background: "var(--paper)",
    alignItems: "center",
    fontSize: 13,
  },
  adminTableHeader: {
    background: "var(--paper-3)",
    fontFamily: "var(--font-mono)",
    fontSize: 10,
    color: "var(--ink-3)",
    letterSpacing: "0.04em",
    textTransform: "uppercase",
  },
  adminNotifItem: {
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
    padding: "12px 14px",
    borderRadius: 10,
    background: "var(--paper)",
    border: "1px solid var(--rule)",
    marginBottom: 8,
  },
  adminNotifDot: {
    width: 8,
    height: 8,
    borderRadius: 999,
    marginTop: 5,
    flexShrink: 0,
  },
  adminNotifBody: {
    minWidth: 0,
  },
  adminNotifTitle: {
    fontSize: 13,
    fontWeight: 500,
    marginBottom: 2,
  },
  adminNotifMeta: {
    fontSize: 11,
    fontFamily: "var(--font-mono)",
    color: "var(--ink-3)",
  },
  adminAuditRow: {
    display: "grid",
    gridTemplateColumns: "120px minmax(0, 1fr) auto",
    gap: 12,
    padding: "9px 12px",
    background: "var(--paper)",
    alignItems: "center",
    fontSize: 13,
    borderBottom: "1px solid var(--rule)",
  },

  // Mobile section
  mobileWrap: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 280px) minmax(0, 1fr)",
    gap: "max(32px, 5vw)",
    alignItems: "center",
  },
  phoneFrame: {
    width: 260,
    height: 520,
    borderRadius: 36,
    border: "3px solid var(--ink)",
    background: "var(--ink)",
    padding: 10,
    boxShadow: "0 30px 80px -30px rgba(0,0,0,0.35)",
    margin: "0 auto",
  },
  phoneScreen: {
    width: "100%",
    height: "100%",
    borderRadius: 26,
    background: "var(--paper-2)",
    overflow: "hidden",
    border: "1px solid var(--rule)",
  },
  phoneNotch: {
    width: 80,
    height: 20,
    borderRadius: "0 0 12px 12px",
    background: "var(--ink)",
    margin: "0 auto",
  },
  phoneBody: {
    padding: "14px 16px 16px",
  },
  phoneTitle: {
    fontFamily: "var(--font-display)",
    fontSize: 18,
    marginBottom: 12,
  },
  phoneScore: {
    fontFamily: "var(--font-display)",
    fontSize: 42,
    color: "var(--accent)",
    lineHeight: 1,
    marginBottom: 4,
  },
  phoneScoreLabel: {
    fontSize: 11,
    fontFamily: "var(--font-mono)",
    color: "var(--ink-3)",
    marginBottom: 14,
  },
  phoneList: {
    display: "grid",
    gap: 8,
  },
  phoneListItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 12px",
    background: "var(--paper-3)",
    borderRadius: 10,
    fontSize: 13,
  },
  mobileFeatures: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 20,
  },
  mobileFeaturePill: {
    padding: "8px 14px",
    borderRadius: 999,
    background: "var(--paper-2)",
    border: "1px solid var(--rule)",
    fontSize: 13,
    fontWeight: 500,
    color: "var(--ink-2)",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
  },

  // AI section
  aiGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 18,
  },
  aiCard: {
    border: "1px solid var(--rule)",
    background: "var(--paper-2)",
    borderRadius: 14,
    padding: "22px 22px 24px",
    minWidth: 0,
    cursor: "default",
  },
  aiCardEmoji: {
    fontSize: 28,
    marginBottom: 14,
    lineHeight: 1,
  },
  aiCardName: {
    fontFamily: "var(--font-display)",
    fontSize: 20,
    marginBottom: 8,
    letterSpacing: "-0.01em",
  },
  aiCardDesc: {
    fontSize: 13.5,
    color: "var(--ink-2)",
    lineHeight: 1.55,
    marginBottom: 14,
  },
  aiBadge: {
    display: "inline-flex",
    padding: "4px 10px",
    borderRadius: 999,
    background: "color-mix(in oklab, var(--accent) 12%, var(--paper-3))",
    color: "var(--accent)",
    fontSize: 11,
    fontFamily: "var(--font-mono)",
    fontWeight: 500,
    letterSpacing: "0.04em",
  },
};

const KPI_COMPONENTS = [
  { pct: "35%", name: "Iqtisodiy samaradorlik", desc: "Rentabellik, 1 ga sof foyda, mehnat samaradorligi — soliq va statistika hisobotlari asosida." },
  { pct: "30%", name: "Agrobiologik samaradorlik", desc: "Hosildorlik, tuproq bonitet bali, almashlab ekish — O'zbekkosmos NDVI mosligida." },
  { pct: "20%", name: "Resurslardan foydalanish", desc: "Suv, energiya, o'g'it samaradorligi — sug'orish hisoblagichlari va tejovchi texnologiya ulushi." },
  { pct: "15%", name: "Moliyaviy intizom", desc: "Soliq to'lovlari, shartnoma intizomi va maqsadli foydalanish ko'rsatkichlari." },
];

const MEZON_GROUPS = [
  {
    title: "1. Iqtisodiy samaradorlik",
    total: "25 ball",
    items: [
      { name: "Rentabellik ≥22%", score: "10 ball" },
      { name: "Rentabellik 15–21%", score: "7 ball" },
      { name: "Rentabellik <15%", score: "4 ball" },
      { name: "1 ga sof foyda — yuqori (+30% hudud o'rtachidan)", score: "8 ball" },
      { name: "1 ga sof foyda — o'rta (+15–29%)", score: "5 ball" },
      { name: "1 ga sof foyda — past (<15%)", score: "2 ball" },
      { name: "Mehnat samaradorligi — norma (1 ga ishchi)", score: "7 ball" },
      { name: "Mehnat samaradorligi — ±20% farq", score: "5 ball" },
      { name: "Mehnat samaradorligi — ≥±20% farq", score: "2 ball" },
    ],
  },
  {
    title: "2. Suv samaradorligi",
    total: "20 ball",
    items: [
      { name: "Suv limitiga rioya — limittan kam", score: "5 ball" },
      { name: "Suv limitiga rioya — limit bo'yicha", score: "3 ball" },
      { name: "Suv limitiga rioya — limittan yuqori", score: "1 ball" },
      { name: "Suv tejovchi texnologiya — to'liq (tomchi, sensor)", score: "7 ball" },
      { name: "Suv tejovchi texnologiya — qisman", score: "4 ball" },
      { name: "Suv tejovchi texnologiya — yo'q", score: "1 ball" },
      { name: "Suv hisobga olish — avtomat (hisoblagich + jurnal)", score: "5 ball" },
      { name: "Suv hisobga olish — faqat jurnal", score: "3 ball" },
      { name: "Suv hisobga olish — yo'q", score: "0 ball" },
    ],
  },
  {
    title: "3. Tuproq va ekologiya",
    total: "15 ball",
    items: [
      { name: "Agrotexnik chora — 3+ usul (organik, siderat, mulcha)", score: "6 ball" },
      { name: "Agrotexnik chora — 1–2 usul", score: "4 ball" },
      { name: "Agrotexnik chora — yo'q", score: "1 ball" },
      { name: "Sho'rlanish nazorati — yaxshilangan", score: "5 ball" },
      { name: "Sho'rlanish nazorati — o'zgarmagan", score: "3 ball" },
      { name: "Sho'rlanish nazorati — yomonlashgan", score: "1 ball" },
      { name: "Eroziya/d degradatsiyaga qarshi — to'liq chora", score: "4 ball" },
      { name: "Eroziya/d degradatsiyaga qarshi — qisman", score: "2 ball" },
      { name: "Eroziya/d degradatsiyaga qarshi — yo'q", score: "0 ball" },
    ],
  },
  {
    title: "4. Yerdan foydalanish",
    total: "15 ball",
    items: [
      { name: "Yerdan foydalanish darajasi ≥85%", score: "15 ball" },
      { name: "Yerdan foydalanish darajasi 70–84%", score: "12 ball" },
      { name: "Yerdan foydalanish darajasi 50–69%", score: "8 ball" },
      { name: "Yerdan foydalanish darajasi <50%", score: "3 ball" },
    ],
  },
  {
    title: "5. Monitoring va hisobot",
    total: "13 ball",
    items: [
      { name: "Monitoring natijalari — oddiy (Excel/daftar)", score: "3 ball" },
      { name: "Monitoring natijalari — yo'q", score: "0 ball" },
      { name: "Hisob-kitob intizomi — to'liq mos", score: "10 ball" },
      { name: "Hisob-kitob intizomi — kichik farqlar", score: "7 ball" },
      { name: "Hisob-kitob intizomi — jiddiy farq", score: "3 ball" },
    ],
  },
  {
    title: "6. Innovatsiya va rivojlanish",
    total: "10 ball",
    items: [
      { name: "Yangi texnologiya (tomchi, yangi nav)", score: "5 ball" },
      { name: "Raqamlashtirish (Excel, CRM, hisob)", score: "3 ball" },
      { name: "O'qish/trening", score: "2 ball" },
    ],
  },
  {
    title: "7. Mehnat samaradorligi",
    total: "5 ball",
    items: [
      { name: "Mehnat samaradorligi — yuqori", score: "5 ball" },
      { name: "Mehnat samaradorligi — o'rta", score: "3 ball" },
      { name: "Mehnat samaradorligi — past", score: "1 ball" },
    ],
  },
];

const WIZARD_DIRECTIONS = [
  { id: "bog", name: "Bog'dorchilik", emoji: "🍎" },
  { id: "sabz", name: "Sabzavot", emoji: "🥕" },
  { id: "chorva", name: "Chorvachilik", emoji: "🐄" },
];

const WIZARD_CONFIG = {
  bog: {
    name: "Bog'dorchilik va uzumchilik",
    categories: [
      {
        name: "Iqtisodiy samaradorlik",
        max: 25,
        questions: [
          { id: "bog_rent", text: "Rentabellik darajasi", options: [
            { label: "≥22%", value: 10 },
            { label: "15–21%", value: 7 },
            { label: "<15%", value: 4 },
          ]},
          { id: "bog_foyda", text: "1 ga sof foyda (hudud o'rtachisiga nisbatan)", options: [
            { label: "Yuqori (+30% dan oshiq)", value: 8 },
            { label: "O'rta (+15–29%)", value: 5 },
            { label: "Past (<15%)", value: 2 },
          ]},
          { id: "bog_mehnat", text: "Mehnat samaradorligi (1 ga ishchi soni)", options: [
            { label: "Norma bo'yicha", value: 7 },
            { label: "±20% farq", value: 5 },
            { label: "≥±20% farq", value: 2 },
          ]},
        ],
      },
      {
        name: "Suv samaradorligi",
        max: 20,
        questions: [
          { id: "bog_suv_limit", text: "Suv limitiga rioya qilinishi", options: [
            { label: "Limittan kam", value: 5 },
            { label: "Limit bo'yicha", value: 3 },
            { label: "Limittan yuqori", value: 1 },
          ]},
          { id: "bog_suv_tech", text: "Suv tejovchi texnologiyalar", options: [
            { label: "To'liq (tomchi, sensor)", value: 7 },
            { label: "Qisman", value: 4 },
            { label: "Yo'q", value: 1 },
          ]},
          { id: "bog_suv_hisob", text: "Suv hisobga olish vositalari", options: [
            { label: "Avtomat (hisoblagich + jurnal)", value: 5 },
            { label: "Faqat jurnal", value: 3 },
            { label: "Yo'q", value: 0 },
          ]},
        ],
      },
      {
        name: "Tuproq va ekologiya",
        max: 15,
        questions: [
          { id: "bog_agro", text: "Agrotexnik chora-tadbirlar", options: [
            { label: "3+ usul (organik, siderat, mulcha)", value: 6 },
            { label: "1–2 usul", value: 4 },
            { label: "Yo'q", value: 1 },
          ]},
          { id: "bog_shor", text: "Sho'rlanish nazorati", options: [
            { label: "Yaxshilangan", value: 5 },
            { label: "O'zgarmagan", value: 3 },
            { label: "Yomonlashgan", value: 1 },
          ]},
          { id: "bog_eroziya", text: "Eroziya va degradatsiyaga qarshi choralar", options: [
            { label: "To'liq", value: 4 },
            { label: "Qisman", value: 2 },
            { label: "Yo'q", value: 0 },
          ]},
        ],
      },
      {
        name: "Yerdan foydalanish",
        max: 15,
        questions: [
          { id: "bog_yer", text: "Yerdan foydalanish darajasi", options: [
            { label: "≥85%", value: 15 },
            { label: "70–84%", value: 12 },
            { label: "50–69%", value: 8 },
            { label: "<50%", value: 3 },
          ]},
        ],
      },
      {
        name: "Monitoring va hisobot",
        max: 13,
        questions: [
          { id: "bog_monitoring", text: "Monitoring natijalarini taqdim etish", options: [
            { label: "Oddiy (Excel/daftar)", value: 3 },
            { label: "Yo'q", value: 0 },
          ]},
          { id: "bog_hisobot", text: "Hisob-kitob intizomi", options: [
            { label: "To'liq mos", value: 10 },
            { label: "Kichik farqlar", value: 7 },
            { label: "Jiddiy farq", value: 3 },
          ]},
        ],
      },
      {
        name: "Innovatsiya va rivojlanish",
        max: 10,
        questions: [
          { id: "bog_innov_tex", text: "Yangi texnologiya (tomchi, yangi nav)", options: [
            { label: "Ha", value: 5 },
            { label: "Yo'q", value: 0 },
          ]},
          { id: "bog_innov_raqam", text: "Raqamlashtirish (Excel, CRM, hisob)", options: [
            { label: "Ha", value: 3 },
            { label: "Yo'q", value: 0 },
          ]},
          { id: "bog_innov_oqish", text: "O'qish/trening", options: [
            { label: "Ha", value: 2 },
            { label: "Yo'q", value: 0 },
          ]},
        ],
      },
      {
        name: "Mehnat samaradorligi",
        max: 5,
        questions: [
          { id: "bog_mehnat2", text: "Mehnat samaradorligi umumiy baho", options: [
            { label: "Yuqori", value: 5 },
            { label: "O'rta", value: 3 },
            { label: "Past", value: 1 },
          ]},
        ],
      },
    ],
  },
  sabz: {
    name: "Sabzavot va polizchilik",
    categories: [
      {
        name: "Suv samaradorligi",
        max: 20,
        questions: [
          { id: "sabz_suv_tech", text: "Suv tejovchi texnologiyalar", options: [
            { label: "To'liq", value: 10 },
            { label: "Qisman", value: 5 },
            { label: "Yo'q", value: 0 },
          ]},
          { id: "sabz_suv_hisob", text: "Suv hisobga olish", options: [
            { label: "Avtomat", value: 5 },
            { label: "Faqat jurnal", value: 3 },
            { label: "Yo'q", value: 0 },
          ]},
          { id: "sabz_suv_limit", text: "Suv limitiga rioya", options: [
            { label: "Limittan kam", value: 5 },
            { label: "Limit bo'yicha", value: 3 },
            { label: "Limittan yuqori", value: 1 },
          ]},
        ],
      },
      {
        name: "Hosildorlik",
        max: 20,
        questions: [
          { id: "sabz_hosildorlik", text: "Normativ hosildorlik darajasi", options: [
            { label: "110% va undan yuqori", value: 10 },
            { label: "85–109%", value: 8 },
            { label: "60–84%", value: 6 },
            { label: "60% dan kam", value: 4 },
          ]},
          { id: "sabz_yer_qiymat", text: "1 ga yerda yaratilgan o'rtacha qiymat", options: [
            { label: "Yuqori", value: 10 },
            { label: "O'rta", value: 5 },
            { label: "Past", value: 0 },
          ]},
        ],
      },
      {
        name: "Rentabellik",
        max: 15,
        questions: [
          { id: "sabz_rent", text: "Rentabellik darajasi", options: [
            { label: "22% dan yuqori", value: 10 },
            { label: "15–21%", value: 8 },
            { label: "10–15%", value: 6 },
            { label: "10% dan kam", value: 4 },
          ]},
        ],
      },
      {
        name: "Yerdan foydalanish",
        max: 15,
        questions: [
          { id: "sabz_yer", text: "Yerdan foydalanish darajasi", options: [
            { label: "≥85%", value: 15 },
            { label: "70–84%", value: 12 },
            { label: "50–69%", value: 8 },
            { label: "<50%", value: 3 },
          ]},
        ],
      },
      {
        name: "Hisobot intizomi",
        max: 10,
        questions: [
          { id: "sabz_hisobot", text: "Soliq va statistika hisobotlari", options: [
            { label: "To'liq mos", value: 10 },
            { label: "Kichik farqlar", value: 7 },
            { label: "Jiddiy farq", value: 3 },
          ]},
        ],
      },
      {
        name: "Innovatsiya",
        max: 10,
        questions: [
          { id: "sabz_innov_tex", text: "Yangi texnologiya", options: [
            { label: "Ha", value: 5 },
            { label: "Yo'q", value: 0 },
          ]},
          { id: "sabz_innov_raqam", text: "Raqamlashtirish", options: [
            { label: "Ha", value: 3 },
            { label: "Yo'q", value: 0 },
          ]},
          { id: "sabz_innov_oqish", text: "O'qish/trening", options: [
            { label: "Ha", value: 2 },
            { label: "Yo'q", value: 0 },
          ]},
        ],
      },
      {
        name: "Mehnat samaradorligi",
        max: 10,
        questions: [
          { id: "sabz_mehnat", text: "Mehnat samaradorligi", options: [
            { label: "Yuqori", value: 5 },
            { label: "O'rta", value: 3 },
            { label: "Past", value: 1 },
          ]},
        ],
      },
    ],
  },
  chorva: {
    name: "Chorvachilik",
    categories: [
      {
        name: "Chorva mahsuldorligi",
        max: 25,
        questions: [
          { id: "chor_sut", text: "Sut mahsuldorligi (hudud eng yuqorisiga nisbatan)", options: [
            { label: "Yuqori", value: 10 },
            { label: "O'rta", value: 5 },
            { label: "Past", value: 0 },
          ]},
          { id: "chor_gosht", text: "Go'sht mahsuldorligi (hudud eng yuqorisiga nisbatan)", options: [
            { label: "Yuqori", value: 10 },
            { label: "O'rta", value: 5 },
            { label: "Past", value: 0 },
          ]},
          { id: "chor_kompleks", text: "Chorvachilik kompleksi mavjudligi", options: [
            { label: "Ha", value: 5 },
            { label: "Yo'q", value: 0 },
          ]},
        ],
      },
      {
        name: "Ozuqa va suv",
        max: 20,
        questions: [
          { id: "chor_ozuqa", text: "Ozuqabob ekin ekilganmi?", options: [
            { label: "Ha", value: 10 },
            { label: "Yo'q", value: 0 },
          ]},
          { id: "chor_suv", text: "Suv samaradorligi", options: [
            { label: "Yuqori", value: 5 },
            { label: "O'rta", value: 3 },
            { label: "Past", value: 1 },
          ]},
          { id: "chor_suv_tech", text: "Suv tejovchi texnologiya", options: [
            { label: "Ha", value: 5 },
            { label: "Yo'q", value: 0 },
          ]},
        ],
      },
      {
        name: "Rentabellik",
        max: 20,
        questions: [
          { id: "chor_rent", text: "Rentabellik darajasi", options: [
            { label: "≥22%", value: 10 },
            { label: "15–21%", value: 7 },
            { label: "10–15%", value: 5 },
            { label: "<10%", value: 2 },
          ]},
          { id: "chor_foyda", text: "1 ga sof foyda", options: [
            { label: "Yuqori", value: 10 },
            { label: "O'rta", value: 5 },
            { label: "Past", value: 0 },
          ]},
        ],
      },
      {
        name: "Yerdan foydalanish",
        max: 15,
        questions: [
          { id: "chor_yer", text: "Yerdan foydalanish darajasi", options: [
            { label: "≥85%", value: 15 },
            { label: "70–84%", value: 12 },
            { label: "50–69%", value: 8 },
            { label: "<50%", value: 3 },
          ]},
        ],
      },
      {
        name: "Hisobot intizomi",
        max: 10,
        questions: [
          { id: "chor_hisobot", text: "Hisobot intizomi", options: [
            { label: "To'liq mos", value: 10 },
            { label: "Kichik farqlar", value: 7 },
            { label: "Jiddiy farq", value: 3 },
          ]},
        ],
      },
      {
        name: "Innovatsiya",
        max: 10,
        questions: [
          { id: "chor_innov", text: "Innovatsiya va rivojlanish", options: [
            { label: "Yangi texnologiya + raqamlashtirish", value: 10 },
            { label: "Qisman", value: 5 },
            { label: "Yo'q", value: 0 },
          ]},
        ],
      },
    ],
  },
};

const CATEGORIES = [
  {
    glyph: "B",
    name: "Bog'dorchilik va uzumchilik",
    desc: "Bog' va tokzor ijarachilariga moslashtirilgan baholash — meva turlari va hududdagi maksimal qiymatga nisbatan.",
    items: ["Suv tejovchi texnologiya", "1 m³ suvga iqtisodiy qiymat", "Normativ hosildorlik", "Rentabellik darajasi"],
  },
  {
    glyph: "S",
    name: "Sabzavot va polizchilik",
    desc: "Yer maydoni va ekin turi bo'yicha eng yuqori hosildorlikka erishgan ijarachiga nisbatan koeffitsientlar.",
    items: ["1 ga yerda yaratilgan qiymat", "Sug'orish hisobi mavjudligi", "Yerdan foydalanish darajasi", "Soliq–statistika muvofiqligi"],
  },
  {
    glyph: "C",
    name: "Chorvachilikka ixtisoslashgan",
    desc: "Go'sht va sut yo'nalishlari alohida — ozuqabob ekin va chorva mahsuldorligi e'tiborga olinadi.",
    items: ["Chorva kompleksi va ozuqa ekin", "1 ga to'g'ri keladigan go'sht/sut", "Veterinariya ma'lumotlari", "Kadastr maydoni mosligi"],
  },
];

const INTEGRATIONS = [
  { name: "Soliq qo'mitasi", what: "Daromad va to'lov hisobotlari" },
  { name: "Statistika qo'mitasi", what: "Hosildorlik va ish hajmi" },
  { name: "Suv xo'jaligi vazirligi", what: "Sug'orish lim. va sarfi" },
  { name: "O'zbekkosmos", what: "NDVI sun'iy yo'ldosh kuzatuvi" },
  { name: "Veterinariya", what: "Chorva soni va sog'liq" },
  { name: "Kadastr agentligi", what: "Yer maydoni va bonitet" },
  { name: "Qishloq xo'jaligi vazirligi", what: "Norm. ko'rsatkichlar" },
];

const ROADMAP = [
  {
    phase: "Bosqich 01",
    name: "Tizim yadrosi",
    items: "Biznes protsesslar tahlili va modellashtirish, texnik topshiriq, UI/UX dizayn, tizim arxitekturasi, ma'lumotlar bazasi strukturasi, mikroservis modullari, autentifikatsiya va avtorizatsiya, rollar va huquqlar boshqaruvi.",
  },
  {
    phase: "Bosqich 02",
    name: "Asosiy modullar",
    items: "Dashboard, ijarachilar reyestri, profil sahifasi, KPI ko'rsatkichlarini boshqarish, ball hisoblash, reyting va monitoring, hisobotlar va analitika, risk tahlili, hududlar/suv/hosildorlik monitoringi, admin panel, sozlamalar, bildirishnomalar, audit log.",
  },
  {
    phase: "Bosqich 03",
    name: "Integratsiyalar va xavfsizlik",
    items: "Soliq, Statistika, Suv xo'jaligi, O'zbekkosmos, Veterinariya, Qishloq xo'jaligi vazirligi, Kadastr bilan API integratsiya, ma'lumot almashinuvi va sinxronizatsiya, axborot xavfsizligi, zaxiralash va tiklash.",
  },
  {
    phase: "Bosqich 04",
    name: "Mobil ilova",
    items: "iOS va Android texnik topshiriq va arxitektura, UI/UX dizayn, autentifikatsiya, mobil dashboard va profil, KPI natijalari, reyting, push bildirishnomalar, offline rejim, hujjat/foto yuklash, geolokatsiya, API integratsiya, App Store va Google Play.",
  },
  {
    phase: "Bosqich 05",
    name: "AI modullar",
    items: "Risk scoring, KPI anomaliyalari, suv-hosildorlik nomuvofiqliklari, O'zbekkosmos ma'lumotlarini AI tahlili, soliq-statistika tafovutlari, AI chatbot, avtomatik xulosa va tavsiyalar, model tayyorlash, optimallashtirish.",
  },
  {
    phase: "Bosqich 06",
    name: "Test va sinov",
    items: "Funksional, integratsion va yuklama testlari, pilot hududlarda sinov, kamchiliklarni bartaraf etish, foydalanuvchi qo'llanmalari, operatorlarni o'qitish.",
  },
  {
    phase: "Bosqich 07",
    name: "Jonli ishga tushirish",
    items: "Production muhitga joylashtirish, uch bosqichli kiberxavfsizlik ekspertizasi, Raqamli hukumat yagona reyestriga kiritish, tizimni ekspluatatsiyaga topshirish, texnik qo'llab-quvvatlash va rivojlantirish.",
  },
];

const NAV_LINKS = [
  { id: "kpi", label: "KPI tizimi" },
  { id: "reyting", label: "Reyting" },
  { id: "platform", label: "Platforma" },
  { id: "monitoring", label: "Monitoring" },
  { id: "qanday-ishlaydi", label: "Qanday ishlaydi" },
  { id: "hisobotlar", label: "Hisobotlar" },
  { id: "risk", label: "Risk" },
  { id: "sinab-koring", label: "Sinab ko'ring" },
  { id: "qarorlar", label: "Qarorlar" },
  { id: "admin", label: "Boshqaruv" },
  { id: "mobil", label: "Mobil" },
  { id: "ai", label: "AI" },
  { id: "savol-javob", label: "FAQ" },
];

function Nav() {
  const [active, setActive] = React.useState(null);

  React.useEffect(() => {
    const ids = NAV_LINKS.map((l) => l.id);
    const targets = ids.map((id) => document.getElementById(id)).filter(Boolean);
    if (targets.length === 0) return;

    // We want the active section to be whichever one's TOP is closest to the
    // top of the viewport (just below the sticky nav). Track the last entry
    // whose intersectionRatio crossed the threshold, fall back to scanning
    // each tick for the section currently containing the nav-bottom line.
    const navHeight = 64;
    const tick = () => {
      const probeY = navHeight + 1;
      let cur = null;
      for (const el of targets) {
        const rect = el.getBoundingClientRect();
        if (rect.top <= probeY && rect.bottom > probeY) {
          cur = el.id;
          break;
        }
      }
      // If we're above the first section, no active state.
      // If we're below the last, sticky to the last id.
      if (!cur) {
        const lastEl = targets[targets.length - 1];
        if (lastEl.getBoundingClientRect().bottom < probeY) {
          cur = lastEl.id;
        }
      }
      setActive(cur);
    };

    tick();
    window.addEventListener("scroll", tick, { passive: true });
    window.addEventListener("resize", tick);
    return () => {
      window.removeEventListener("scroll", tick);
      window.removeEventListener("resize", tick);
    };
  }, []);

  const handleClick = (id) => (e) => {
    const el = document.getElementById(id);
    if (!el) return;
    e.preventDefault();
    const navHeight = 64;
    const top = el.getBoundingClientRect().top + window.scrollY - navHeight - 8;
    window.scrollTo({ top, behavior: "smooth" });
    history.replaceState(null, "", "#" + id);
  };

  return React.createElement(
    "nav",
    { style: styles.nav },
    React.createElement(
      "div",
      { style: styles.navInner },
      React.createElement(
        "a",
        { href: "#", onClick: (e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }, style: { ...styles.brand, cursor: "pointer" } },
        React.createElement("span", { style: styles.brandMark }, "AN"),
        React.createElement("span", null, "Agro Nazorat")
      ),
      React.createElement(
        "div",
        { style: styles.navLinks, "data-mobile-hide": true },
        ...NAV_LINKS.map((l) =>
          React.createElement(
            "a",
            {
              key: l.id,
              href: "#" + l.id,
              "data-link": true,
              "data-active": active === l.id ? "true" : undefined,
              onClick: handleClick(l.id),
              style: active === l.id ? { color: "var(--ink)" } : undefined,
            },
            l.label
          )
        )
      ),
      React.createElement(
        "div",
        { style: { display: "flex", gap: 8, alignItems: "center" } },
        React.createElement("button", { style: styles.btnGhost, "data-mobile-hide": true }, "Kirish"),
        React.createElement(
          "button",
          { style: styles.btnSolid },
          "Demo so'rash ",
          React.createElement("span", { "data-arrow": true }, "→")
        )
      )
    )
  );
}

function Hero() {
  return React.createElement(
    "section",
    { id: "main", style: styles.hero, "data-screen-label": true, "data-grid-stack-mobile": true },
    React.createElement(
      "div",
      null,
      React.createElement(
        "div",
        { style: styles.heroEyebrow },
        React.createElement("span", { style: styles.dot }),
        React.createElement("span", null, "Vazirlar Mahkamasi qarori loyihasi")
      ),
      React.createElement(
        "h1",
        { style: styles.h1 },
        "Yerdan foydalanishni ",
        React.createElement("em", { style: styles.h1Em }, "ko'rsatkichlar"),
        " baholaydi."
      ),
      React.createElement(
        "p",
        { style: styles.lede },
        "Agro Nazorat — qishloq xo'jaligi yer ijarachilarining iqtisodiy, agrobiologik, resurs va moliyaviy samaradorligini KPI tizimi orqali baholaydigan davlat platformasi. Soliq, statistika, suv xo'jaligi va sun'iy yo'ldosh ma'lumotlari bir joyda."
      ),
      React.createElement(
        "div",
        { style: styles.heroCtaRow },
        React.createElement(
          "button",
          { style: styles.heroCta },
          "Demo so'rash ",
          React.createElement("span", { "data-arrow": true }, "→")
        ),
        React.createElement("a", { href: "#kpi", style: styles.heroCta2 }, "KPI tizimi haqida")
      )
    ),
    React.createElement(HeroPreview, null)
  );
}

function HeroPreview() {
  const components = [
    { name: "Iqtisodiy samaradorlik", weight: "0.35", score: "31.5" },
    { name: "Agrobiologik samaradorlik", weight: "0.30", score: "24.0" },
    { name: "Resurslardan foydalanish", weight: "0.20", score: "16.6" },
    { name: "Moliyaviy intizom", weight: "0.15", score: "11.7" },
  ];
  return React.createElement(
    "div",
    { style: styles.previewWrap },
    React.createElement(
      "div",
      { style: styles.previewWindow },
      React.createElement(
        "div",
        { style: styles.previewChrome },
        React.createElement("span", { style: { ...styles.dotSm, background: "#ff5f56" } }),
        React.createElement("span", { style: { ...styles.dotSm, background: "#ffbd2e" } }),
        React.createElement("span", { style: { ...styles.dotSm, background: "#27c93f" } }),
        React.createElement("span", { style: { marginLeft: 12, fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--ink-3)" } }, "agro-nazorat.uz / ijarachi / 04231")
      ),
      React.createElement(
        "div",
        { style: styles.previewBody },
        React.createElement("div", { style: styles.previewLabel }, "Yer ijarachisi"),
        React.createElement("div", { style: styles.previewName }, "Andijon Bog'dorchilik MChJ"),
        React.createElement("div", { style: styles.previewMeta }, "Andijon vil. · 142 ga · Bog'dorchilik · 2026-yil 1-chorak"),
        React.createElement(
          "div",
          { style: styles.scoreRow },
          React.createElement(
            "div",
            { style: styles.scoreNum },
            React.createElement("span", { "data-score-rise": true }, "83.8")
          ),
          React.createElement("div", { style: styles.scoreOf }, "/ 100 · O'rta toifa")
        ),
        React.createElement(
          "div",
          { style: styles.scoreBar },
          React.createElement("div", { style: { ...styles.scoreBarFill, width: "83.8%" }, "data-bar-fill": true })
        ),
        React.createElement(
          "div",
          { style: styles.componentsList },
          ...components.map((c, i) =>
            React.createElement(
              "div",
              { key: i, "data-stagger": true, style: { ...styles.componentRow, "--i": i + 4 } },
              React.createElement("span", { style: styles.componentName }, c.name),
              React.createElement("span", { style: styles.componentWeight }, "×", c.weight),
              React.createElement("span", { style: styles.componentScore }, c.score)
            )
          )
        )
      )
    )
  );
}

function KpiSection() {
  return React.createElement(
    "section",
    { id: "kpi", style: styles.section },
    React.createElement("div", { style: styles.sectionEyebrow }, "01 · KPI tizimi"),
    React.createElement(
      "h2",
      { style: styles.sectionH2 },
      "To'rt taraf, bitta indeks."
    ),
    React.createElement(
      "p",
      { style: styles.sectionLede },
      "Har bir taraf alohida hisoblanadi va og'irlik bilan yagona indeksga aylantiriladi. 86–100 — yuqori toifa, 71–85 — o'rta, 55–70 — xavf guruhi, 55 dan past — chora ko'riladi."
    ),
    React.createElement(
      "div",
      { style: styles.kpiGrid, "data-grid-dim": true, "data-grid-2-mobile": true },
      ...KPI_COMPONENTS.map((k, i) =>
        React.createElement(
          "div",
          { key: i, "data-card": true, "data-stagger": true, style: { ...styles.kpiCard, "--i": i } },
          React.createElement("div", { style: styles.kpiPct }, k.pct),
          React.createElement("div", { style: styles.kpiName }, k.name),
          React.createElement("div", { style: styles.kpiDesc }, k.desc)
        )
      )
    )
  );
}

const RANK_DATA = [
  { name: "Andijon Bog'dorchilik MChJ", meta: "Andijon · Bog'dorchilik", score: 92.3, color: "oklch(0.62 0.16 152)" },
  { name: "Olmazor Sabzavotchilik", meta: "Toshkent · Sabzavot", score: 88.7, color: "oklch(0.62 0.16 152)" },
  { name: "Farg'ona Premium Uzum", meta: "Farg'ona · Uzumchilik", score: 84.1, color: "oklch(0.7 0.14 95)" },
  { name: "Samarqand Chorva Klasteri", meta: "Samarqand · Chorvachilik", score: 76.2, color: "oklch(0.7 0.14 95)" },
  { name: "Sirdaryo Aylanma", meta: "Sirdaryo · Sabzavot", score: 64.8, color: "oklch(0.66 0.16 60)" },
];

const RISK_DATA = [
  { name: "Buxoro Sabzavot LTD", meta: "Buxoro · Sabzavot", flag: "Hosildorlik o'rtachadan +180% — g'ayritabiiy", level: "high" },
  { name: "Qarshi Bog' MChJ", meta: "Qashqadaryo · Bog'dorchilik", flag: "Suv sarfi o'tgan chorakning 3.1× ko'p", level: "high" },
  { name: "Navoiy Chorva XK", meta: "Navoiy · Chorvachilik", flag: "Soliq vs statistika hisoboti farqi 22%", level: "med" },
  { name: "Jizzax Polizchilik", meta: "Jizzax · Polizchilik", flag: "NDVI mart oyida keskin pasaygan", level: "med" },
  { name: "Surxondaryo Aylanma", meta: "Surxondaryo · Sabzavot", flag: "Hisobot 31 kun kechikkan", level: "low" },
];

const REGION_DATA = [
  { name: "Andijon", score: 81.5 },
  { name: "Toshkent", score: 78.2 },
  { name: "Farg'ona", score: 76.4 },
  { name: "Samarqand", score: 73.8 },
  { name: "Buxoro", score: 71.0 },
  { name: "Surxondaryo", score: 64.3 },
];

const EXPORT_OPTIONS = [
  { fmt: "CSV", name: "Reyting jadvali", meta: "1,847 satr · 12 ustun", size: "284 KB" },
  { fmt: "PDF", name: "Chorak hisoboti", meta: "Q1 2026 · 18 sahifa", size: "1.2 MB" },
  { fmt: "XLSX", name: "Tahliliy hisobot", meta: "Komponent kesimida", size: "612 KB" },
  { fmt: "API", name: "JSON ulanish", meta: "REST endpoint · OAuth2", size: "rate: 60/min" },
];

const RANK_TABS = ["Reyting", "Risk", "Hududlar", "Eksport"];

function RankPreviewBody({ tab }) {
  if (tab === "Reyting") {
    return React.createElement(
      "div",
      { style: styles.rankBody, key: "reyting" },
      React.createElement(
        "div",
        { style: styles.rankFilterRow },
        React.createElement("div", { style: styles.rankRegion }, "Barcha viloyat · Barcha yo'nalish"),
        React.createElement("div", { style: styles.rankSortBtn }, "ball ↓")
      ),
      React.createElement(
        "div",
        { style: styles.rankList },
        ...RANK_DATA.map((r, i) =>
          React.createElement(
            "div",
            { key: i, style: { ...styles.rankRow, "--i": i }, "data-stagger": true },
            React.createElement("span", { style: styles.rankNum }, "#", String(i + 1).padStart(2, "0")),
            React.createElement(
              "div",
              { style: styles.rankBody2 },
              React.createElement("div", { style: styles.rankNameLine }, r.name),
              React.createElement("div", { style: styles.rankMetaLine }, r.meta)
            ),
            React.createElement(
              "div",
              { style: styles.rankMiniBar },
              React.createElement("div", { style: { ...styles.rankMiniBarFill, width: `${r.score}%`, background: r.color } })
            ),
            React.createElement("span", { style: styles.rankScore }, r.score.toFixed(1))
          )
        )
      )
    );
  }

  if (tab === "Risk") {
    const levelColor = {
      high: "oklch(0.62 0.18 25)",
      med: "oklch(0.7 0.16 60)",
      low: "oklch(0.78 0.14 95)",
    };
    const levelLabel = { high: "yuqori", med: "o'rta", low: "past" };
    return React.createElement(
      "div",
      { style: styles.rankBody, key: "risk" },
      React.createElement(
        "div",
        { style: styles.rankFilterRow },
        React.createElement("div", { style: styles.rankRegion }, "5 anomaliya · oxirgi 30 kun"),
        React.createElement("div", { style: styles.rankSortBtn }, "yangilik ↓")
      ),
      React.createElement(
        "div",
        { style: styles.rankList },
        ...RISK_DATA.map((r, i) =>
          React.createElement(
            "div",
            {
              key: i,
              "data-stagger": true,
              style: {
                display: "grid",
                gridTemplateColumns: "4px minmax(0, 1fr) auto",
                gap: 12,
                padding: "12px 0 12px 0",
                borderTop: "1px solid var(--rule)",
                alignItems: "center",
                "--i": i,
              },
            },
            React.createElement("span", { style: { width: 4, height: 36, background: levelColor[r.level], borderRadius: 2, marginLeft: -4 } }),
            React.createElement(
              "div",
              { style: { minWidth: 0 } },
              React.createElement("div", { style: styles.rankNameLine }, r.name),
              React.createElement("div", { style: { ...styles.rankMetaLine, marginTop: 4, color: "var(--ink-2)" } }, r.flag)
            ),
            React.createElement(
              "span",
              {
                style: {
                  fontFamily: "var(--font-mono)",
                  fontSize: 11,
                  padding: "3px 8px",
                  borderRadius: 999,
                  background: "color-mix(in oklab, " + levelColor[r.level] + " 18%, var(--paper))",
                  color: levelColor[r.level],
                  border: "1px solid color-mix(in oklab, " + levelColor[r.level] + " 35%, var(--rule))",
                  textTransform: "lowercase",
                },
              },
              levelLabel[r.level]
            )
          )
        )
      )
    );
  }

  if (tab === "Hududlar") {
    const max = Math.max(...REGION_DATA.map((r) => r.score));
    return React.createElement(
      "div",
      { style: styles.rankBody, key: "hududlar" },
      React.createElement(
        "div",
        { style: styles.rankFilterRow },
        React.createElement("div", { style: styles.rankRegion }, "14 viloyat · o'rtacha KPI"),
        React.createElement("div", { style: styles.rankSortBtn }, "ball ↓")
      ),
      React.createElement(
        "div",
        { style: { display: "grid", gap: 12, paddingTop: 4 } },
        ...REGION_DATA.map((r, i) =>
          React.createElement(
            "div",
            {
              key: i,
              "data-stagger": true,
              style: { display: "grid", gridTemplateColumns: "100px minmax(0, 1fr) 50px", gap: 12, alignItems: "center", "--i": i },
            },
            React.createElement("span", { style: { fontSize: 13, color: "var(--ink-2)" } }, r.name),
            React.createElement(
              "div",
              { style: { height: 8, background: "var(--paper-3)", borderRadius: 999, overflow: "hidden" } },
              React.createElement("div", {
                style: {
                  height: "100%",
                  width: `${(r.score / max) * 100}%`,
                  background: r.score >= 75 ? "oklch(0.62 0.16 152)" : r.score >= 70 ? "oklch(0.78 0.14 95)" : "oklch(0.7 0.16 60)",
                  borderRadius: 999,
                },
              })
            ),
            React.createElement("span", { style: { fontFamily: "var(--font-mono)", fontSize: 13, color: "var(--ink)", textAlign: "right", fontWeight: 500 } }, r.score.toFixed(1))
          )
        )
      )
    );
  }

  if (tab === "Eksport") {
    return React.createElement(
      "div",
      { style: styles.rankBody, key: "eksport" },
      React.createElement(
        "div",
        { style: { ...styles.rankFilterRow, marginBottom: 12 } },
        React.createElement("div", { style: styles.rankRegion }, "Yuklab olish formatlari"),
        React.createElement("div", { style: styles.rankSortBtn }, "Q1 2026")
      ),
      React.createElement(
        "div",
        { style: { display: "grid", gap: 8, paddingTop: 4 } },
        ...EXPORT_OPTIONS.map((e, i) =>
          React.createElement(
            "div",
            {
              key: i,
              "data-stagger": true,
              "data-card": true,
              style: {
                display: "grid",
                gridTemplateColumns: "52px minmax(0, 1fr) auto",
                gap: 12,
                padding: "12px 14px",
                border: "1px solid var(--rule)",
                borderRadius: 10,
                alignItems: "center",
                background: "var(--paper)",
                cursor: "pointer",
                "--i": i,
              },
            },
            React.createElement(
              "span",
              {
                style: {
                  fontFamily: "var(--font-mono)",
                  fontSize: 11,
                  padding: "4px 8px",
                  background: "var(--paper-3)",
                  border: "1px solid var(--rule)",
                  borderRadius: 6,
                  textAlign: "center",
                  color: "var(--accent)",
                  fontWeight: 600,
                  letterSpacing: "0.04em",
                },
              },
              e.fmt
            ),
            React.createElement(
              "div",
              { style: { minWidth: 0 } },
              React.createElement("div", { style: { fontSize: 14, fontWeight: 500, color: "var(--ink)" } }, e.name),
              React.createElement("div", { style: { fontSize: 11.5, color: "var(--ink-3)", fontFamily: "var(--font-mono)", marginTop: 2 } }, e.meta)
            ),
            React.createElement("span", { style: { fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--ink-3)" } }, e.size)
          )
        )
      )
    );
  }

  return null;
}

function RankSection() {
  const [tab, setTab] = React.useState("Reyting");

  // Auto-rotate when not hovered, so the section feels alive on landing.
  const [hovered, setHovered] = React.useState(false);
  React.useEffect(() => {
    if (hovered) return;
    const id = setInterval(() => {
      setTab((cur) => RANK_TABS[(RANK_TABS.indexOf(cur) + 1) % RANK_TABS.length]);
    }, 4500);
    return () => clearInterval(id);
  }, [hovered]);

  return React.createElement(
    "section",
    { id: "reyting", style: styles.section },
    React.createElement(
      "div",
      { style: styles.rankWrap, "data-grid-stack-mobile": true },
      React.createElement(
        "div",
        { style: styles.rankCopy },
        React.createElement("div", { style: styles.sectionEyebrow }, "02 · Reyting jonli holda"),
        React.createElement(
          "h2",
          { style: styles.sectionH2 },
          "Eng yaxshi ijarachilar — bir ko'rinishda."
        ),
        React.createElement(
          "p",
          { style: styles.sectionLede },
          "Ballar har chorakda yangilanadi va hududlar kesimida solishtiriladi. Reyting, anomaliyalar, hududlar ko'rinishi va eksport — bir oynada."
        ),
        React.createElement(
          "div",
          { style: { display: "flex", gap: 20, fontSize: 13, color: "var(--ink-2)", marginTop: 24 } },
          React.createElement(
            "div",
            null,
            React.createElement("div", { style: { fontFamily: "var(--font-display)", fontSize: 28, color: "var(--ink)" } }, "1,847"),
            React.createElement("div", { style: { fontSize: 12, color: "var(--ink-3)", marginTop: 2, fontFamily: "var(--font-mono)" } }, "ijarachi")
          ),
          React.createElement(
            "div",
            null,
            React.createElement("div", { style: { fontFamily: "var(--font-display)", fontSize: 28, color: "var(--ink)" } }, "14"),
            React.createElement("div", { style: { fontSize: 12, color: "var(--ink-3)", marginTop: 2, fontFamily: "var(--font-mono)" } }, "viloyat")
          ),
          React.createElement(
            "div",
            null,
            React.createElement("div", { style: { fontFamily: "var(--font-display)", fontSize: 28, color: "var(--ink)" } }, "Q1 2026"),
            React.createElement("div", { style: { fontSize: 12, color: "var(--ink-3)", marginTop: 2, fontFamily: "var(--font-mono)" } }, "joriy chorak")
          )
        )
      ),
      React.createElement(
        "div",
        {
          style: styles.rankWindow,
          onMouseEnter: () => setHovered(true),
          onMouseLeave: () => setHovered(false),
        },
        React.createElement(
          "div",
          { style: styles.previewChrome },
          React.createElement("span", { style: { ...styles.dotSm, background: "#ff5f56" } }),
          React.createElement("span", { style: { ...styles.dotSm, background: "#ffbd2e" } }),
          React.createElement("span", { style: { ...styles.dotSm, background: "#27c93f" } }),
          React.createElement("span", { style: { marginLeft: 12, fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--ink-3)" } }, "agro-nazorat.uz / reyting")
        ),
        React.createElement(
          "div",
          { style: styles.rankTabs, role: "tablist" },
          ...RANK_TABS.map((t) =>
            React.createElement(
              "button",
              {
                key: t,
                role: "tab",
                "aria-selected": tab === t,
                onClick: () => setTab(t),
                style: { ...styles.rankTab, ...(tab === t ? styles.rankTabActive : {}), cursor: "pointer" },
              },
              t
            )
          )
        ),
        React.createElement(
          "div",
          { "data-tab-content": true, key: tab },
          React.createElement(RankPreviewBody, { tab })
        )
      )
    )
  );
}

function CategoriesSection() {
  return React.createElement(
    "section",
    { id: "kategoriyalar", style: styles.section },
    React.createElement("div", { style: styles.sectionEyebrow }, "03 · Yo'nalishlar"),
    React.createElement(
      "h2",
      { style: styles.sectionH2 },
      "Har bir ijarachi o'z mezonida."
    ),
    React.createElement(
      "p",
      { style: styles.sectionLede },
      "Bog' va uzumzor, sabzavotchilik, chorvachilik — har biriga moslashtirilgan baholash daraxti. Hududdagi eng yuqori ko'rsatkichli ijarachiga nisbatan koeffitsientlarda hisoblanadi."
    ),
    React.createElement(
      "div",
      { style: styles.catGrid, "data-grid-dim": true, "data-grid-stack-mobile": true },
      ...CATEGORIES.map((c, i) =>
        React.createElement(
          "div",
          { key: i, "data-card": true, "data-stagger": true, style: { ...styles.catCard, "--i": i } },
          React.createElement("div", { style: styles.catGlyph }, c.glyph),
          React.createElement("div", { style: styles.catName }, c.name),
          React.createElement("p", { style: styles.catDesc }, c.desc),
          React.createElement(
            "ul",
            { style: styles.catList },
            ...c.items.map((it, j) =>
              React.createElement(
                "li",
                { key: j, style: styles.catItem },
                React.createElement("span", { style: { position: "absolute", left: 0, color: "var(--accent)" } }, "·"),
                it
              )
            )
          )
        )
      )
    )
  );
}

function IntegrationsSection() {
  return React.createElement(
    "section",
    { id: "integratsiya", style: styles.section },
    React.createElement("div", { style: styles.sectionEyebrow }, "04 · Integratsiyalar"),
    React.createElement(
      "h2",
      { style: styles.sectionH2 },
      "Bir manba emas — hammasi."
    ),
    React.createElement(
      "p",
      { style: styles.sectionLede },
      "Ijarachi haqidagi ma'lumot davlat axborot tizimlaridan, sun'iy yo'ldosh kuzatuvidan va sug'orish hisoblagichlaridan avtomatik tarzda yig'iladi. Kiritish kerak emas — tekshirish kerak."
    ),
    React.createElement(
      "div",
      { style: styles.intGrid },
      ...INTEGRATIONS.map((it, i) =>
        React.createElement(
          "div",
          { key: i, style: styles.intCell },
          React.createElement("span", { style: styles.intCellName }, it.name),
          React.createElement("span", { style: styles.intCellWhat }, it.what)
        )
      )
    )
  );
}

const OUTCOMES = [
  {
    color: "oklch(0.62 0.16 152)",
    tier: "Yuqori toifa · 86–100",
    name: "Rag'batlantirish",
    desc: "Soliq imtiyozlari, subsidiyalar, kredit shartlarini yengillashtirish. Eng samarali ijarachilar nishonga aylanadi.",
    range: "Imtiyoz",
  },
  {
    color: "oklch(0.78 0.14 95)",
    tier: "O'rta toifa · 71–85",
    name: "Kuzatuv ostida",
    desc: "Faoliyat qoniqarli — saqlab qolinadi, lekin keyingi chorakda yaxshilanish kutiladi. Yo'l-yo'riq beriladi.",
    range: "Kuzatuv",
  },
  {
    color: "oklch(0.7 0.16 60)",
    tier: "Xavf guruhi · 55–70",
    name: "Ogohlantirish",
    desc: "Rasmiy ogohlantirish va kamchiliklarni bartaraf etish muddati. Keyingi baholashda yana past natija bo'lsa — chora.",
    range: "Ogohlantirish",
  },
  {
    color: "oklch(0.62 0.18 25)",
    tier: "Qoniqarsiz · < 55",
    name: "Shartnomani qayta ko'rib chiqish",
    desc: "Jarima, ijara shartnomasini qayta ko'rib chiqish, vaziyat to'g'rilanmasa — yer uchastkasini qaytarib olish tashabbusi.",
    range: "Chora",
  },
];

const MISOL_ROWS = [
  {
    name: "Iqtisodiy samaradorlik",
    weight: "0.35 og'irlik",
    inputs: [
      { k: "Rentabellik", v: "28%" },
      { k: "Sof foyda 1ga", v: "142M so'm" },
      { k: "Hudud o'rtacha", v: "110M so'm" },
    ],
    formula: "Rent>22% + foyda hudud+29% → 90/100",
    score: "31.5",
    of: "/ 35",
  },
  {
    name: "Agrobiologik samaradorlik",
    weight: "0.30 og'irlik",
    inputs: [
      { k: "Hosildorlik", v: "norm 110%" },
      { k: "Tuproq holati", v: "yaxshilangan" },
      { k: "Almashlab ekish", v: "to'liq" },
    ],
    formula: "Hosildorlik norm+10% + tuproq + rotatsiya → 80/100",
    score: "24.0",
    of: "/ 30",
  },
  {
    name: "Resurslardan foydalanish",
    weight: "0.20 og'irlik",
    inputs: [
      { k: "Suv tejovchi tech", v: "80% maydon" },
      { k: "Suv hisoblagich", v: "avtomat + jurnal" },
      { k: "1 m³ → qiymat", v: "yuqori" },
    ],
    formula: "Tomchi 80% + avtomat hisob + samaradorlik → 83/100",
    score: "16.6",
    of: "/ 20",
  },
  {
    name: "Moliyaviy intizom",
    weight: "0.15 og'irlik",
    inputs: [
      { k: "Soliq to'lovi", v: "to'liq, vaqtida" },
      { k: "Hisobot mosligi", v: "kichik fark" },
      { k: "Shartnoma", v: "maqsadli" },
    ],
    formula: "Soliq + hisobot mosligi + maqsadli → 78/100",
    score: "11.7",
    of: "/ 15",
  },
];

function MisolSection() {
  return React.createElement(
    "section",
    { id: "misol", style: styles.section },
    React.createElement("div", { style: styles.sectionEyebrow }, "06 · Misol"),
    React.createElement(
      "h2",
      { style: styles.sectionH2 },
      "Real raqamlardan kelib chiqib, 83.8."
    ),
    React.createElement(
      "p",
      { style: styles.sectionLede },
      "Andijonlik bog'dorchilik ijarachisi misolida. Har bir komponent uchun kirish ma'lumoti, formula va ball — pastda yagona indeksga jamlanadi."
    ),
    React.createElement(
      "div",
      { style: styles.misolWrap },
      React.createElement(
        "div",
        { style: styles.misolHeader },
        React.createElement("div", { style: styles.misolHeaderName }, "Andijon Bog'dorchilik MChJ"),
        React.createElement("div", { style: styles.misolHeaderMeta }, "142 ga · Bog'dorchilik · 2026 Q1")
      ),
      ...MISOL_ROWS.map((r, i) =>
        React.createElement(
          "div",
          {
            key: i,
            "data-stagger": true,
            style: { ...styles.misolRow, ...(i === MISOL_ROWS.length - 1 ? styles.misolRowLast : {}), "--i": i },
          },
          React.createElement(
            "div",
            null,
            React.createElement("div", { style: styles.misolCompName }, r.name),
            React.createElement("div", { style: styles.misolWeight }, r.weight)
          ),
          React.createElement(
            "div",
            { style: styles.misolInputs },
            ...r.inputs.map((inp, j) =>
              React.createElement(
                "div",
                { key: j, style: styles.misolInputRow },
                React.createElement("span", { style: styles.misolInputKey }, inp.k),
                React.createElement("span", { style: styles.misolInputVal }, inp.v)
              )
            )
          ),
          React.createElement("div", { style: styles.misolFormula }, r.formula),
          React.createElement(
            "div",
            { style: { textAlign: "right", minWidth: 90 } },
            React.createElement("span", { style: styles.misolScore }, r.score),
            React.createElement("span", { style: styles.misolScoreOf }, " ", r.of)
          )
        )
      ),
      React.createElement(
        "div",
        { style: styles.misolFooter },
        React.createElement(
          "div",
          { style: styles.misolFinalFormula },
          "KPI = 0.35×90 + 0.30×80 + 0.20×83 + 0.15×78",
          React.createElement("br", null),
          "    = 31.5 + 24.0 + 16.6 + 11.7"
        ),
        React.createElement(
          "div",
          { style: styles.misolFinalScore },
          React.createElement("span", { style: styles.misolFinalNum }, "83.8"),
          React.createElement("span", { style: styles.misolFinalTier }, "O'rta toifa · 71–85")
        )
      )
    )
  );
}

function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }

// Map raw input to a 0–100 component score using the rubric in the KPI nizom.
function rentToIq(rent) {
  // <15 → 40, 15-21 → 70, 22+ → 100; smooth via piecewise lerp.
  if (rent < 15) return 40 + (rent / 15) * 20; // 0..15 → 40..60
  if (rent < 22) return 60 + ((rent - 15) / 7) * 25; // 15..22 → 60..85
  return clamp(85 + ((rent - 22) / 8) * 15, 0, 100); // 22..30 → 85..100
}
function hosToAs(hos) {
  // <60 → 30, 60-84 → 60, 85-109 → 80, 110+ → 100
  if (hos < 60) return 20 + (hos / 60) * 20; // → 20..40
  if (hos < 85) return 40 + ((hos - 60) / 25) * 25; // → 40..65
  if (hos < 110) return 65 + ((hos - 85) / 25) * 25; // → 65..90
  return clamp(90 + ((hos - 110) / 40) * 10, 0, 100); // → 90..100
}
function suvToRfs(suv) {
  return clamp(20 + (suv / 100) * 80, 0, 100); // 0%→20, 100%→100
}
function solToMi(sol) {
  return clamp(20 + (sol / 100) * 80, 0, 100); // 0%→20, 100%→100
}

function tierFor(score) {
  if (score >= 86) return { name: "Yuqori toifa", color: "oklch(0.62 0.16 152)", bg: "oklch(0.92 0.06 152)" };
  if (score >= 71) return { name: "O'rta toifa", color: "oklch(0.6 0.14 95)", bg: "oklch(0.94 0.06 95)" };
  if (score >= 55) return { name: "Xavf guruhi", color: "oklch(0.6 0.16 60)", bg: "oklch(0.94 0.07 60)" };
  return { name: "Chora ko'riladi", color: "oklch(0.55 0.18 25)", bg: "oklch(0.94 0.06 25)" };
}

function WizardSection() {
  const [direction, setDirection] = React.useState("bog");
  const [answers, setAnswers] = React.useState({});
  const [result, setResult] = React.useState(null);

  const config = WIZARD_CONFIG[direction];

  const setAnswer = (qid, value) => {
    setAnswers((prev) => ({ ...prev, [qid]: value }));
    setResult(null);
  };

  const calculate = () => {
    let total = 0;
    const breakdown = [];
    for (const cat of config.categories) {
      let catScore = 0;
      for (const q of cat.questions) {
        const val = answers[q.id];
        if (val !== undefined) catScore += val;
      }
      total += catScore;
      breakdown.push({ name: cat.name, score: catScore, max: cat.max });
    }
    setResult({ total, breakdown, tier: tierFor(total) });
  };

  const answeredCount = Object.keys(answers).length;
  const totalQuestions = config.categories.reduce((s, c) => s + c.questions.length, 0);
  const progress = Math.round((answeredCount / totalQuestions) * 100);

  return React.createElement(
    "section",
    { id: "sinab-koring", style: styles.section },
    React.createElement("div", { style: styles.sectionEyebrow }, "07 · Baholash"),
    React.createElement("h2", { style: styles.sectionH2 }, "Yo'nalishingizni tanlang va baholang."),
    React.createElement(
      "p",
      { style: styles.sectionLede },
      "Rasmiy KPI mezonlari asosida o'z ballingizni hisoblang. Har bir savolga javob bering va umumiy natijani ko'ring."
    ),
    React.createElement(
      "div",
      { style: { display: "flex", gap: 10, marginBottom: 32, flexWrap: "wrap" } },
      ...WIZARD_DIRECTIONS.map((d) =>
        React.createElement(
          "button",
          {
            key: d.id,
            onClick: () => { setDirection(d.id); setAnswers({}); setResult(null); },
            style: {
              ...styles.wizardTab,
              ...(direction === d.id ? styles.wizardTabActive : {}),
            },
          },
          d.emoji,
          " ",
          d.name
        )
      )
    ),
    React.createElement(
      "div",
      { style: { marginBottom: 28 } },
      React.createElement(
        "div",
        { style: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, fontSize: 12, fontFamily: "var(--font-mono)", color: "var(--ink-3)" } },
        React.createElement("span", null, "Javob berildi: ", answeredCount, " / ", totalQuestions),
        React.createElement("span", null, progress, "%")
      ),
      React.createElement(
        "div",
        { style: { height: 4, background: "var(--paper-3)", borderRadius: 999, overflow: "hidden" } },
        React.createElement("div", { style: { height: "100%", width: progress + "%", background: "var(--accent)", borderRadius: 999, transition: "width 0.3s ease" } })
      )
    ),
    React.createElement(
      "div",
      { style: styles.wizardGrid, "data-grid-dim": true, "data-grid-stack-mobile": true },
      ...config.categories.map((cat, ci) =>
        React.createElement(
          "div",
          { key: ci, "data-card": true, "data-stagger": true, style: { ...styles.wizardCard, "--i": ci } },
          React.createElement(
            "div",
            { style: styles.wizardCatHeader },
            React.createElement("span", { style: styles.wizardCatName }, cat.name),
            React.createElement("span", { style: styles.wizardCatMax }, cat.max, " ball")
          ),
          ...cat.questions.map((q, qi) =>
            React.createElement(
              "div",
              { key: qi, style: { ...styles.wizardQuestion, ...(qi === cat.questions.length - 1 ? { marginBottom: 0 } : {}) } },
              React.createElement("div", { style: styles.wizardQText }, q.text),
              React.createElement(
                "div",
                { style: { display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 } },
                ...q.options.map((opt, oi) =>
                  React.createElement(
                    "button",
                    {
                      key: oi,
                      onClick: () => setAnswer(q.id, opt.value),
                      style: {
                        ...styles.wizardOption,
                        ...(answers[q.id] === opt.value ? styles.wizardOptionActive : {}),
                      },
                    },
                    opt.label,
                    " · ",
                    opt.value,
                    " ball"
                  )
                )
              )
            )
          )
        )
      )
    ),
    React.createElement(
      "div",
      { style: { marginTop: 32, display: "flex", justifyContent: "center" } },
      React.createElement(
        "button",
        {
          onClick: calculate,
          style: {
            ...styles.btnSolid,
            padding: "14px 32px",
            fontSize: 16,
            opacity: answeredCount === totalQuestions ? 1 : 0.5,
          },
          disabled: answeredCount !== totalQuestions,
        },
        "Hisoblash ",
        React.createElement("span", { "data-arrow": true }, "→")
      )
    ),
    result &&
      React.createElement(
        "div",
        { style: { ...styles.wizardResult, marginTop: 40 }, "data-screen-label": true },
        React.createElement(
          "div",
          { style: { display: "flex", alignItems: "baseline", gap: 16, marginBottom: 20, flexWrap: "wrap" } },
          React.createElement("span", { style: { ...styles.wizardResultScore, color: result.tier.color } }, result.total.toFixed(1)),
          React.createElement(
            "span",
            {
              style: {
                fontSize: 14,
                fontWeight: 600,
                padding: "6px 14px",
                borderRadius: 999,
                background: result.tier.bg,
                color: result.tier.color,
                border: "1px solid color-mix(in oklab, " + result.tier.color + " 30%, var(--rule))",
              },
            },
            result.tier.name
          )
        ),
        React.createElement(
          "div",
          { style: { display: "grid", gap: 10 } },
          ...result.breakdown.map((b, bi) =>
            React.createElement(
              "div",
              { key: bi, style: { display: "grid", gridTemplateColumns: "1fr 120px 50px", gap: 12, alignItems: "center", fontSize: 13 } },
              React.createElement("span", { style: { color: "var(--ink-2)" } }, b.name),
              React.createElement(
                "div",
                { style: { height: 6, background: "var(--paper-3)", borderRadius: 999, overflow: "hidden" } },
                React.createElement("div", { style: { height: "100%", width: (b.max ? (b.score / b.max) * 100 : 0) + "%", background: "var(--accent)", borderRadius: 999 } })
              ),
              React.createElement("span", { style: { fontFamily: "var(--font-mono)", color: "var(--ink-3)", textAlign: "right" } }, b.score, "/", b.max)
            )
          )
        )
      )
  );
}

function OutcomesSection() {
  return React.createElement(
    "section",
    { id: "qarorlar", style: styles.section },
    React.createElement("div", { style: styles.sectionEyebrow }, "08 · Qarorlar"),
    React.createElement(
      "h2",
      { style: styles.sectionH2 },
      "Ball — qaror demakdir."
    ),
    React.createElement(
      "p",
      { style: styles.sectionLede },
      "KPI natijasi bo'yicha ijarachi to'rt yo'nalishdan biriga tushadi: rag'bat, kuzatuv, ogohlantirish yoki shartnomani qayta ko'rib chiqish. Qaror manbalari avtomatik, ammo bajarilishi vakolatli organ tomonidan tasdiqlanadi."
    ),
    React.createElement(
      "div",
      { style: styles.outcomeGrid, "data-grid-dim": true, "data-grid-2-mobile": true },
      ...OUTCOMES.map((o, i) =>
        React.createElement(
          "div",
          { key: i, "data-card": true, "data-stagger": true, style: { ...styles.outcomeCard, "--i": i } },
          React.createElement("div", { style: { ...styles.outcomeBand, background: o.color } }),
          React.createElement(
            "div",
            { style: styles.outcomeBody },
            React.createElement("div", { style: styles.outcomeTier }, o.tier),
            React.createElement("div", { style: styles.outcomeName }, o.name),
            React.createElement("p", { style: styles.outcomeDesc }, o.desc),
            React.createElement("span", { style: styles.outcomeRange }, o.range)
          )
        )
      )
    )
  );
}

function FlowSection() {
  const sources = ["Soliq", "Statistika", "Suv x'oj", "O'zbekkosmos", "Veterinariya", "Kadastr", "Hisoblagich"];
  const tiers = [
    { color: "oklch(0.68 0.16 152)", name: "Yuqori toifa", range: "86–100" },
    { color: "oklch(0.78 0.14 95)", name: "O'rta toifa", range: "71–85" },
    { color: "oklch(0.72 0.16 60)", name: "Xavf guruhi", range: "55–70" },
    { color: "oklch(0.62 0.18 25)", name: "Chora ko'riladi", range: "< 55" },
  ];
  return React.createElement(
    "section",
    { id: "qanday-ishlaydi", style: styles.section },
    React.createElement("div", { style: styles.sectionEyebrow }, "05 · Qanday ishlaydi"),
    // 06 · Qarorlar follows in OutcomesSection
    React.createElement(
      "h2",
      { style: styles.sectionH2 },
      "Manbadan reytinggacha — uch bosqich."
    ),
    React.createElement(
      "p",
      { style: styles.sectionLede },
      "Ma'lumot davlat tizimlaridan avtomatik yig'iladi, formula bo'yicha hisoblanadi va to'rt toifaga ajratiladi. Ijarachi ortidan hech kim hisobot to'plamaydi."
    ),
    React.createElement(
      "div",
      { style: styles.flowGrid, "data-grid-stack-mobile": true, id: "flow-grid" },
      // Stage 1: collection
      React.createElement(
        "div",
        { style: styles.flowStage },
        React.createElement("div", { style: styles.flowStageNum }, "01 · Yig'ish"),
        React.createElement("div", { style: styles.flowStageName }, "Avtomatik manbalar"),
        React.createElement("p", { style: styles.flowStageDesc }, "Soliq hisobotlari, statistika, sug'orish hisoblagichlari, sun'iy yo'ldosh kuzatuvi va kadastr bazalari API orqali."),
        React.createElement(
          "div",
          { style: styles.flowChips },
          ...sources.map((s, i) => React.createElement("span", { key: i, style: styles.flowChip }, s))
        )
      ),
      React.createElement(
        "div",
        { style: styles.flowConnector, "data-flow-connector": true },
        React.createElement("span", { "data-arrow": true }, "→")
      ),
      // Stage 2: calculation
      React.createElement(
        "div",
        { style: styles.flowStage },
        React.createElement("div", { style: styles.flowStageNum }, "02 · Hisoblash"),
        React.createElement("div", { style: styles.flowStageName }, "KPI formulasi"),
        React.createElement("p", { style: styles.flowStageDesc }, "To'rt taraf — iqtisodiy, agrobiologik, resurs va moliyaviy — qat'iy og'irliklar bilan yagona indeksga aylantiriladi."),
        React.createElement(
          "div",
          { style: styles.flowFormula },
          "KPI = ",
          React.createElement("span", { style: styles.flowFormulaEm }, "0.35"),
          "·Iq + ",
          React.createElement("span", { style: styles.flowFormulaEm }, "0.30"),
          "·As + ",
          React.createElement("span", { style: styles.flowFormulaEm }, "0.20"),
          "·Rfs + ",
          React.createElement("span", { style: styles.flowFormulaEm }, "0.15"),
          "·Mi"
        )
      ),
      React.createElement(
        "div",
        { style: styles.flowConnector, "data-flow-connector": true },
        React.createElement("span", { "data-arrow": true }, "→")
      ),
      // Stage 3: rating
      React.createElement(
        "div",
        { style: styles.flowStage },
        React.createElement("div", { style: styles.flowStageNum }, "03 · Reyting"),
        React.createElement("div", { style: styles.flowStageName }, "To'rt toifa"),
        React.createElement("p", { style: styles.flowStageDesc }, "Yakuniy ball ijarachini to'rt toifadan biriga ajratadi. Toifaga qarab rag'batlantirish, ogohlantirish yoki shartnomani qayta ko'rib chiqish."),
        React.createElement(
          "div",
          { style: styles.flowTiers },
          ...tiers.map((t, i) =>
            React.createElement(
              "div",
              { key: i, style: styles.flowTier },
              React.createElement("span", { style: { ...styles.flowTierBand, background: t.color } }),
              React.createElement("span", { style: styles.flowTierName }, t.name),
              React.createElement("span", { style: styles.flowTierRange }, t.range)
            )
          )
        )
      )
    )
  );
}

function RoadmapSection() {
  return React.createElement(
    "section",
    { id: "yo-l-xarita", style: styles.section },
    React.createElement("div", { style: styles.sectionEyebrow }, "09 · Yo'l xarita"),
    React.createElement(
      "h2",
      { style: styles.sectionH2 },
      "Yetti bosqich, bitta platforma."
    ),
    React.createElement(
      "p",
      { style: styles.sectionLede },
      "Tizim yadrosidan AI modullari va mobil ilovagacha — bosqichma-bosqich. Har bir bosqich keyingisining asosi."
    ),
    React.createElement(
      "div",
      { style: styles.roadGrid, "data-roadmap-grid": true },
      ...ROADMAP.flatMap((r, i) => [
        React.createElement("div", { key: `p${i}`, style: styles.roadPhaseLabel, "data-roadmap-phase": true }, r.phase),
        React.createElement(
          "div",
          { key: `c${i}`, "data-card": true, "data-stagger": true, style: { ...styles.roadCard, "--i": i } },
          React.createElement("div", { style: styles.roadName }, r.name),
          React.createElement("div", { style: styles.roadItems }, r.items)
        ),
      ])
    )
  );
}

const FOOTER_COLS = [
  {
    title: "Loyiha",
    links: ["Vazirlar Mahkamasi qarori", "KPI nizom", "KPI hisoblash uslubi", "Yo'l xarita"],
  },
  {
    title: "Hamkor idoralar",
    links: ["Qishloq xo'jaligi vazirligi", "Suv xo'jaligi vazirligi", "Soliq qo'mitasi", "O'zbekkosmos"],
  },
  {
    title: "Bog'lanish",
    links: ["info@agro-nazorat.uz", "+998 71 000 00 00", "Toshkent sh., Mustaqillik", "Texnik yordam"],
  },
];

function LangSwitch() {
  const [active, setActive] = React.useState("UZ");
  const langs = ["UZ", "RU", "EN"];
  return React.createElement(
    "div",
    { style: styles.langSwitch, role: "group", "aria-label": "Til tanlash" },
    ...langs.map((l) =>
      React.createElement(
        "button",
        {
          key: l,
          style: { ...styles.langBtn, ...(active === l ? styles.langBtnActive : {}) },
          onClick: () => setActive(l),
          "aria-pressed": active === l,
        },
        l
      )
    )
  );
}

const FAQ = [
  {
    q: "Hisoboti to'liq bo'lmagan ijarachi qanday baholanadi?",
    a: "Tegishli komponent bo'yicha 0 ball qo'yiladi va umumiy indeks shu pasaytirilgan holda hisoblanadi. Ko'p hollarda hisobot etishmovchiligining o'zi past KPI sabab bo'lib, ogohlantirish chorasiga olib keladi. Maqsad — ijarachilarni hisobot intizomiga rag'batlantirish.",
  },
  {
    q: "NDVI nima va qanday foydalaniladi?",
    a: "NDVI (Normalized Difference Vegetation Index) — sun'iy yo'ldosh tasvirlaridan o'simlik qoplamasini sonli baholash usuli. O'zbekkosmos har 5–10 kunda yangilangan tasvirlardan ekin maydoni faolligini avtomatik aniqlaydi va platforma uni hosildorlik dalili sifatida ishlatadi.",
  },
  {
    q: "Past ball — darhol jarima demakmi?",
    a: "Yo'q. Tizim bosqichma-bosqich: 71–85 — kuzatuv, 55–70 — rasmiy ogohlantirish va kamchilikni bartaraf etish muddati. Faqat keyingi baholashda ham past natija saqlansa va kamchilik bartaraf etilmasa — jarima yoki shartnomani qayta ko'rib chiqish chorasi qo'llaniladi.",
  },
  {
    q: "Norm hosildorlik qanday belgilanadi?",
    a: "Tuproqning bonitet bali, kadastr bahosi va hududning tabiiy-iqlim sharoitidan kelib chiqib, agrotexnik tadbirlar asosida olinishi kutilayotgan o'rtacha hosil miqdori. Ya'ni har bir uchastkada \"oqilona kutilayotgan\" hosildorlik o'lchovi mavjud — undan kam ham, ko'p ham ball ta'sir qiladi.",
  },
  {
    q: "Ijarachi ballga e'tiroz bildira oladimi?",
    a: "Ha. Har bir ijarachi o'z shaxsiy kabinetida ball tarkibini, manbalarini va hisoblash mantig'ini ko'ra oladi. E'tiroz bo'lsa — vakolatli organga rasmiy murojaat asosida ma'lumot qayta tekshiriladi. Manba xato berdimi yoki formulada nuqson bormi — barcha qaror jurnallashtiriladi.",
  },
  {
    q: "Ma'lumotlar qanday himoyalanadi?",
    a: "Tizim Raqamli hukumatning axborot tizimlari va resurslari yagona reyestriga muvofiq joriy etiladi: uch bosqichli kiberxavfsizlik ekspertizasi, vakolatli idoralar darajasida ulanish, har bir foydalanuvchi harakati audit log'ga yoziladi. Maxfiy moliyaviy ma'lumot ijarachining o'zi va vakolatli organdan boshqa hech kimga ko'rsatilmaydi.",
  },
];

function FaqSection() {
  return React.createElement(
    "section",
    { id: "savol-javob", style: styles.section },
    React.createElement("div", { style: styles.sectionEyebrow }, "10 · Savol-javob"),
    React.createElement(
      "h2",
      { style: styles.sectionH2 },
      "Tez-tez beriladigan savollar."
    ),
    React.createElement(
      "p",
      { style: styles.sectionLede },
      "Ijarachilar va ekspertlar ko'p so'ragan savollarga qisqa javoblar."
    ),
    React.createElement(
      "div",
      { style: styles.faqList },
      ...FAQ.map((f, i) =>
        React.createElement(
          "details",
          {
            key: i,
            style: { ...styles.faqItem, ...(i === FAQ.length - 1 ? styles.faqItemLast : {}) },
          },
          React.createElement(
            "summary",
            { style: styles.faqSummary },
            React.createElement("span", null, f.q),
            React.createElement("span", { style: styles.faqMark, "data-faq-mark": true }, "+")
          ),
          React.createElement("div", { style: styles.faqAnswer }, f.a)
        )
      )
    )
  );
}

function Footer() {
  return React.createElement(
    "footer",
    { style: styles.footer },
    React.createElement(
      "div",
      { style: styles.footerInner },
      React.createElement(
        "div",
        { style: styles.footerGrid, "data-grid-stack-mobile": true },
        React.createElement(
          "div",
          { style: styles.footerBrandCol },
          React.createElement(
            "div",
            { style: styles.footerBrandRow },
            React.createElement("span", { style: styles.brandMark }, "AN"),
            React.createElement("span", null, "Agro Nazorat")
          ),
          React.createElement(
            "p",
            { style: styles.footerTagline },
            "Qishloq xo'jaligi yer ijarachilarini KPI asosida baholaydigan davlat platformasi. Vazirlar Mahkamasi qarori loyihasi asosida ishlab chiqilmoqda."
          ),
          React.createElement(LangSwitch, null)
        ),
        ...FOOTER_COLS.map((col, i) =>
          React.createElement(
            "div",
            { key: i, style: styles.footerCol },
            React.createElement("div", { style: styles.footerColTitle }, col.title),
            React.createElement(
              "ul",
              { style: styles.footerLinks },
              ...col.links.map((link, j) =>
                React.createElement(
                  "li",
                  { key: j },
                  React.createElement("span", { style: styles.footerLink, "data-link": true }, link)
                )
              )
            )
          )
        )
      ),
      React.createElement(
        "div",
        { style: styles.footerBar },
        React.createElement("div", null, "© ", new Date().getFullYear(), " · Agro Nazorat platformasi"),
        React.createElement("div", null, "Yer ijarachilari uchun · Toshkent")
      )
    )
  );
}

function MezonSection() {
  return React.createElement(
    "section",
    { id: "mezonlar", style: styles.section },
    React.createElement("div", { style: styles.sectionEyebrow }, "M · Bog'dorchilik mezonlari"),
    React.createElement("h2", { style: styles.sectionH2 }, "Rasmiy baholash jadvali."),
    React.createElement(
      "p",
      { style: styles.sectionLede },
      "Vazirlar Mahkamasi qarori loyihasi asosida bog'dorchilik va uzumchilik ijarachilari uchun belgilangan KPI mezonlari. Har bir ijarachi o'z yo'nalishi bo'yicha maksimal ballarga intiladi."
    ),
    React.createElement(
      "div",
      { style: styles.mezonGrid, "data-grid-dim": true, "data-grid-stack-mobile": true },
      ...MEZON_GROUPS.map((g, i) =>
        React.createElement(
          "div",
          { key: i, "data-card": true, "data-stagger": true, style: { ...styles.mezonCard, "--i": i } },
          React.createElement(
            "div",
            { style: styles.mezonHeader },
            React.createElement("span", { style: styles.mezonTitle }, g.title),
            React.createElement("span", { style: styles.mezonTotal }, g.total)
          ),
          ...g.items.map((it, j) =>
            React.createElement(
              "div",
              {
                key: j,
                style: {
                  ...styles.mezonItem,
                  ...(j === g.items.length - 1 ? styles.mezonItemLast : {}),
                },
              },
              React.createElement("span", { style: styles.mezonItemName }, it.name),
              React.createElement("span", { style: styles.mezonItemScore }, it.score)
            )
          )
        )
      )
    ),
    React.createElement(
      "p",
      { style: { ...styles.sectionLede, marginTop: 28, fontSize: 14, color: "var(--ink-3)", marginBottom: 0 } },
      "KPI tizimi iqtisodiy (25), suv (20), tuproq (15), yer (15), monitoring (3), hisobot intizomi (10), innovatsiya (10) va mehnat (5) komponentlaridan iborat. Monitoring uchun qimmat IoT talab qilinmaydi — oddiy Excel yoki daftar ham ball beradi."
    )
  );
}

function SvgAreaChart({ data, width, height, color }) {
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const step = width / (data.length - 1);
  let path = `M 0,${height}`;
  data.forEach((v, i) => {
    const x = i * step;
    const y = height - ((v - min) / range) * (height - 4) - 2;
    path += ` L ${x},${y}`;
  });
  path += ` L ${width},${height} Z`;
  let linePath = "";
  data.forEach((v, i) => {
    const x = i * step;
    const y = height - ((v - min) / range) * (height - 4) - 2;
    linePath += (i === 0 ? "M" : "L") + ` ${x},${y}`;
  });
  return React.createElement(
    "svg",
    { width, height, viewBox: `0 0 ${width} ${height}`, style: { display: "block" } },
    React.createElement("path", { d: path, fill: color, opacity: 0.15 }),
    React.createElement("path", { d: linePath, fill: "none", stroke: color, strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" })
  );
}

function SvgLineChart({ data, width, height, color }) {
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const step = width / (data.length - 1);
  let path = "";
  data.forEach((v, i) => {
    const x = i * step;
    const y = height - ((v - min) / range) * (height - 8) - 4;
    path += (i === 0 ? "M" : "L") + ` ${x},${y}`;
  });
  return React.createElement(
    "svg",
    { width, height, viewBox: `0 0 ${width} ${height}`, style: { display: "block" } },
    React.createElement("path", { d: path, fill: "none", stroke: color, strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" })
  );
}

function SvgBarChart({ data, width, height, color }) {
  const max = Math.max(...data.map((d) => d.value));
  const barWidth = (width / data.length) * 0.7;
  const gap = (width / data.length) * 0.3;
  return React.createElement(
    "svg",
    { width, height, viewBox: `0 0 ${width} ${height}`, style: { display: "block" } },
    ...data.map((d, i) => {
      const barHeight = (d.value / max) * (height - 20);
      const x = i * (barWidth + gap) + gap / 2;
      const y = height - barHeight - 14;
      return React.createElement(
        React.Fragment,
        { key: i },
        React.createElement("rect", { x, y, width: barWidth, height: barHeight, fill: color, opacity: 0.5 + (i / data.length) * 0.5, rx: 3 }),
        React.createElement("text", { x: x + barWidth / 2, y: height - 2, textAnchor: "middle", fontSize: 8, fontFamily: "var(--font-mono)", fill: "var(--ink-3)" }, d.label)
      );
    })
  );
}

function PlatformSection() {
  const kpiTiles = [
    { label: "Jami ijarachilar", value: "1,847", change: "+12", changeColor: "var(--accent)", sub: "2026-yil 1-chorak" },
    { label: "O'rtacha KPI", value: "74.2", change: "+3.1", changeColor: "var(--accent)", sub: "/ 100 ball" },
    { label: "Yuqori toifa", value: "324", change: "+18", changeColor: "var(--accent)", sub: "17.5%" },
    { label: "Xavf guruhi", value: "186", change: "-4", changeColor: "oklch(0.55 0.18 25)", sub: "10.1%" },
  ];
  const recentAlerts = [
    { time: "14:32", text: "Sirdaryo Aylanma — suv limiti 18% oshiq", level: "high" },
    { time: "13:15", text: "Qashqadaryo — hosildorlik o'rtachadan 35% past", level: "med" },
    { time: "11:48", text: "Buxoro Sabzavot — soliq vs statistika 22% farq", level: "high" },
    { time: "09:22", text: "Andijon Bog'dorchilik — yangi hisobot qabul qilindi", level: "low" },
  ];
  const scoreData = [68, 72, 70, 75, 74, 78, 76, 80, 79, 82, 81, 83];
  return React.createElement(
    "section",
    { id: "platform", style: styles.section },
    React.createElement("div", { style: styles.sectionEyebrow }, "04 · Platforma"),
    React.createElement("h2", { style: styles.sectionH2 }, "Bitta tizim — barcha ma'lumotlar."),
    React.createElement("p", { style: styles.sectionLede }, "Dashboard, ijarachilar reyestri va har bir ijarachi profili — barchasi bir joyda. Ma'lumotlarni qidirish, filtrlash va solishtirish endi bir necha soniya."),
    React.createElement(
      "div",
      { style: { border: "1px solid var(--rule)", borderRadius: 14, background: "var(--paper-2)", overflow: "hidden", boxShadow: "0 20px 60px -30px rgba(0,0,0,0.2)" } },
      // Header
      React.createElement(
        "div",
        { style: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px", borderBottom: "1px solid var(--rule)", background: "var(--paper-3)" } },
        React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 10 } },
          React.createElement("span", { style: { ...styles.dotSm, background: "#ff5f56" } }),
          React.createElement("span", { style: { ...styles.dotSm, background: "#ffbd2e" } }),
          React.createElement("span", { style: { ...styles.dotSm, background: "#27c93f" } }),
          React.createElement("span", { style: { fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--ink-3)", marginLeft: 8 } }, "agro-nazorat.uz / dashboard / 2026-Q1")
        ),
        React.createElement("div", { style: { display: "flex", gap: 8, alignItems: "center" } },
          React.createElement("span", { style: { padding: "5px 10px", background: "var(--paper)", border: "1px solid var(--rule)", borderRadius: 6, fontSize: 11, fontFamily: "var(--font-mono)", color: "var(--ink-3)" } }, "🔍 Qidirish..."),
          React.createElement("span", { style: { padding: "5px 10px", background: "var(--paper)", border: "1px solid var(--rule)", borderRadius: 6, fontSize: 11, fontFamily: "var(--font-mono)", color: "var(--ink-3)" } }, "📅 2026-Q1"),
          React.createElement("span", { style: { padding: "5px 10px", background: "var(--paper)", border: "1px solid var(--rule)", borderRadius: 6, fontSize: 11, fontFamily: "var(--font-mono)", color: "var(--ink-3)" } }, "📍 Barcha hududlar")
        )
      ),
      // Body
      React.createElement(
        "div",
        { style: { padding: "20px", display: "grid", gridTemplateColumns: "minmax(0, 1fr) 280px", gap: 20 } },
        // Left column
        React.createElement(
          "div",
          { style: { display: "grid", gap: 16 } },
          // KPI tiles
          React.createElement(
            "div",
            { style: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 } },
            ...kpiTiles.map((k, i) =>
              React.createElement(
                "div",
                { key: i, style: { background: "var(--paper)", border: "1px solid var(--rule)", borderRadius: 10, padding: "14px 16px" } },
                React.createElement("div", { style: { fontSize: 11, fontFamily: "var(--font-mono)", color: "var(--ink-3)", marginBottom: 6 } }, k.label),
                React.createElement("div", { style: { display: "flex", alignItems: "baseline", gap: 8 } },
                  React.createElement("span", { style: { fontFamily: "var(--font-display)", fontSize: 28, letterSpacing: "-0.02em" } }, k.value),
                  React.createElement("span", { style: { fontFamily: "var(--font-mono)", fontSize: 12, color: k.changeColor, fontWeight: 500 } }, k.change)
                ),
                React.createElement("div", { style: { fontSize: 11, color: "var(--ink-3)", marginTop: 2 } }, k.sub)
              )
            )
          ),
          // Chart + table row
          React.createElement(
            "div",
            { style: { display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 320px)", gap: 16 } },
            // Chart
            React.createElement(
              "div",
              { style: { background: "var(--paper)", border: "1px solid var(--rule)", borderRadius: 10, padding: 16 } },
              React.createElement("div", { style: { fontSize: 13, fontWeight: 500, marginBottom: 12 } }, "KPI dinamikasi (2025–2026)"),
              React.createElement(SvgAreaChart, { data: scoreData, width: 500, height: 140, color: "var(--accent)" }),
              React.createElement("div", { style: { display: "flex", justifyContent: "space-between", fontSize: 10, fontFamily: "var(--font-mono)", color: "var(--ink-3)", marginTop: 8 } },
                React.createElement("span", null, "2025-Q1"),
                React.createElement("span", null, "2025-Q2"),
                React.createElement("span", null, "2025-Q3"),
                React.createElement("span", null, "2025-Q4"),
                React.createElement("span", null, "2026-Q1")
              )
            ),
            // Registry mini-table
            React.createElement(
              "div",
              { style: { background: "var(--paper)", border: "1px solid var(--rule)", borderRadius: 10, padding: 16 } },
              React.createElement("div", { style: { fontSize: 13, fontWeight: 500, marginBottom: 12 } }, "Top 5 ijarachi"),
              React.createElement(
                "div",
                { style: { display: "grid", gap: 1, background: "var(--rule)", borderRadius: 8, overflow: "hidden", border: "1px solid var(--rule)" } },
                React.createElement(
                  "div",
                  { style: { display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto auto", gap: 10, padding: "7px 10px", background: "var(--paper-3)", fontFamily: "var(--font-mono)", fontSize: 9, color: "var(--ink-3)", letterSpacing: "0.04em", textTransform: "uppercase" } },
                  React.createElement("span", null, "Nomi"),
                  React.createElement("span", null, "Yo'nalish"),
                  React.createElement("span", null, "Ball")
                ),
                ...[
                  { name: "Andijon Bog'dorchilik MChJ", dir: "Bog'dorchilik", score: "92.3", color: "var(--accent)" },
                  { name: "Olmazor Sabzavotchilik", dir: "Sabzavot", score: "88.7", color: "var(--accent)" },
                  { name: "Farg'ona Premium Uzum", dir: "Uzumchilik", score: "84.1", color: "oklch(0.7 0.14 95)" },
                  { name: "Samarqand Chorva Klasteri", dir: "Chorvachilik", score: "76.2", color: "oklch(0.7 0.14 95)" },
                  { name: "Sirdaryo Aylanma", dir: "Sabzavot", score: "64.8", color: "oklch(0.66 0.16 60)" },
                ].map((r, i) =>
                  React.createElement(
                    "div",
                    { key: i, style: { display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto auto", gap: 10, padding: "8px 10px", background: "var(--paper)", alignItems: "center", fontSize: 11, borderBottom: i < 4 ? "1px solid var(--rule)" : "none" } },
                    React.createElement("span", { style: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } }, r.name),
                    React.createElement("span", { style: { fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--ink-3)" } }, r.dir),
                    React.createElement("span", { style: { fontFamily: "var(--font-mono)", fontSize: 11, fontWeight: 500, color: r.color } }, r.score)
                  )
                )
              )
            )
          )
        ),
        // Right column - alerts
        React.createElement(
          "div",
          { style: { display: "grid", gap: 12, alignContent: "start" } },
          React.createElement("div", { style: { fontSize: 13, fontWeight: 500 } }, "So'nggi ogohlantirishlar"),
          ...recentAlerts.map((a, i) =>
            React.createElement(
              "div",
              { key: i, style: { padding: "10px 12px", background: "var(--paper)", border: "1px solid var(--rule)", borderRadius: 8, borderLeft: `3px solid ${a.level === "high" ? "oklch(0.55 0.18 25)" : a.level === "med" ? "oklch(0.7 0.16 60)" : "var(--accent)"}` } },
              React.createElement("div", { style: { fontSize: 10, fontFamily: "var(--font-mono)", color: "var(--ink-3)", marginBottom: 3 } }, a.time),
              React.createElement("div", { style: { fontSize: 12, color: "var(--ink-2)", lineHeight: 1.4 } }, a.text)
            )
          ),
          React.createElement(
            "div",
            { style: { padding: "14px", background: "var(--paper)", border: "1px solid var(--rule)", borderRadius: 10 } },
            React.createElement("div", { style: { fontSize: 13, fontWeight: 500, marginBottom: 10 } }, "Hududlar kesimida"),
            ...[
              { name: "Andijon", score: 81.5 },
              { name: "Toshkent", score: 78.2 },
              { name: "Farg'ona", score: 76.4 },
              { name: "Samarqand", score: 73.8 },
              { name: "Buxoro", score: 71.0 },
              { name: "Sirdaryo", score: 64.3 },
            ].map((r, i) =>
              React.createElement(
                "div",
                { key: i, style: { display: "flex", alignItems: "center", gap: 8, marginBottom: 6 } },
                React.createElement("span", { style: { fontSize: 11, width: 60 } }, r.name),
                React.createElement(
                  "div",
                  { style: { flex: 1, height: 5, borderRadius: 999, background: "var(--paper-3)", overflow: "hidden" } },
                  React.createElement("div", { style: { width: `${r.score}%`, height: "100%", borderRadius: 999, background: "var(--accent)", opacity: 0.5 + (i / 6) * 0.5 } })
                ),
                React.createElement("span", { style: { fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--ink-3)", width: 28, textAlign: "right" } }, r.score)
              )
            )
          )
        )
      )
    )
  );
}

function MonitoringSection() {
  const waterData = [2450, 2380, 2520, 2610, 2580, 2720, 2810, 2950, 3020, 3100, 3180, 3240];
  const yieldData = [95, 98, 102, 105, 108, 110, 112, 115, 118, 120, 122, 124];
  const profitData = [22.5, 21.8, 20.5, 19.2, 18.5, 17.8, 17.2, 16.5, 15.8, 15.2, 14.6, 14.2];
  return React.createElement(
    "section",
    { id: "monitoring", style: styles.section },
    React.createElement("div", { style: styles.sectionEyebrow }, "05 · Monitoring"),
    React.createElement("h2", { style: styles.sectionH2 }, "Real vaqtda nazorat."),
    React.createElement("p", { style: styles.sectionLede }, "Suv sarfi, hosildorlik, rentabellik va hududlar kesimidagi ko'rsatkichlar avtomatik yig'iladi va real vaqtda yangilanadi."),
    React.createElement(
      "div",
      { style: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 18 } },
      // Water monitoring
      React.createElement(
        "div",
        { style: { border: "1px solid var(--rule)", background: "var(--paper-2)", borderRadius: 14, padding: "22px 24px 24px" } },
        React.createElement(
          "div",
          { style: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 } },
          React.createElement("div", { style: { fontFamily: "var(--font-display)", fontSize: 20 } }, "Suv sarfi"),
          React.createElement("span", { style: { fontSize: 11, fontFamily: "var(--font-mono)", padding: "4px 10px", borderRadius: 999, background: "color-mix(in oklab, oklch(0.55 0.18 25) 12%, var(--paper-3))", color: "oklch(0.55 0.18 25)" } }, "Limit oshiq")
        ),
        React.createElement("div", { style: { display: "flex", alignItems: "baseline", gap: 10, marginBottom: 4 } },
          React.createElement("span", { style: { fontFamily: "var(--font-display)", fontSize: 36, lineHeight: 1, color: "oklch(0.55 0.18 25)" } }, "3,240"),
          React.createElement("span", { style: { fontFamily: "var(--font-mono)", fontSize: 13, color: "oklch(0.55 0.18 25)", fontWeight: 500 } }, "+18%"),
          React.createElement("span", { style: { fontSize: 12, color: "var(--ink-3)" } }, "m³ / chorak")
        ),
        React.createElement("div", { style: { fontSize: 12, color: "var(--ink-3)", marginBottom: 14 } }, "Limit: 2,750 m³ · o'tgan chorak: 2,750 m³"),
        React.createElement(SvgAreaChart, { data: waterData, width: 520, height: 120, color: "oklch(0.55 0.18 25)" }),
        React.createElement("div", { style: { display: "flex", justifyContent: "space-between", fontSize: 10, fontFamily: "var(--font-mono)", color: "var(--ink-3)", marginTop: 8 } },
          React.createElement("span", null, "Yan"),
          React.createElement("span", null, "Mar"),
          React.createElement("span", null, "Iyun"),
          React.createElement("span", null, "Sen"),
          React.createElement("span", null, "Dek")
        )
      ),
      // Yield monitoring
      React.createElement(
        "div",
        { style: { border: "1px solid var(--rule)", background: "var(--paper-2)", borderRadius: 14, padding: "22px 24px 24px" } },
        React.createElement(
          "div",
          { style: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 } },
          React.createElement("div", { style: { fontFamily: "var(--font-display)", fontSize: 20 } }, "Hosildorlik"),
          React.createElement("span", { style: { fontSize: 11, fontFamily: "var(--font-mono)", padding: "4px 10px", borderRadius: 999, background: "color-mix(in oklab, var(--accent) 12%, var(--paper-3))", color: "var(--accent)" } }, "Normadan yuqori")
        ),
        React.createElement("div", { style: { display: "flex", alignItems: "baseline", gap: 10, marginBottom: 4 } },
          React.createElement("span", { style: { fontFamily: "var(--font-display)", fontSize: 36, lineHeight: 1, color: "var(--accent)" } }, "112%"),
          React.createElement("span", { style: { fontFamily: "var(--font-mono)", fontSize: 13, color: "var(--accent)", fontWeight: 500 } }, "+8%"),
          React.createElement("span", { style: { fontSize: 12, color: "var(--ink-3)" } }, "normativga nisbatan")
        ),
        React.createElement("div", { style: { fontSize: 12, color: "var(--ink-3)", marginBottom: 14 } }, "Maqsad: 100% · o'tgan yil: 104%"),
        React.createElement(SvgAreaChart, { data: yieldData, width: 520, height: 120, color: "var(--accent)" }),
        React.createElement("div", { style: { display: "flex", justifyContent: "space-between", fontSize: 10, fontFamily: "var(--font-mono)", color: "var(--ink-3)", marginTop: 8 } },
          React.createElement("span", null, "Yan"),
          React.createElement("span", null, "Mar"),
          React.createElement("span", null, "Iyun"),
          React.createElement("span", null, "Sen"),
          React.createElement("span", null, "Dek")
        )
      ),
      // Profitability monitoring
      React.createElement(
        "div",
        { style: { border: "1px solid var(--rule)", background: "var(--paper-2)", borderRadius: 14, padding: "22px 24px 24px" } },
        React.createElement(
          "div",
          { style: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 } },
          React.createElement("div", { style: { fontFamily: "var(--font-display)", fontSize: 20 } }, "Rentabellik"),
          React.createElement("span", { style: { fontSize: 11, fontFamily: "var(--font-mono)", padding: "4px 10px", borderRadius: 999, background: "color-mix(in oklab, oklch(0.78 0.14 95) 18%, var(--paper-3))", color: "oklch(0.65 0.14 95)" } }, "Maqsaddan past")
        ),
        React.createElement("div", { style: { display: "flex", alignItems: "baseline", gap: 10, marginBottom: 4 } },
          React.createElement("span", { style: { fontFamily: "var(--font-display)", fontSize: 36, lineHeight: 1, color: "oklch(0.65 0.14 95)" } }, "14.2%"),
          React.createElement("span", { style: { fontFamily: "var(--font-mono)", fontSize: 13, color: "oklch(0.55 0.18 25)", fontWeight: 500 } }, "-3.1%"),
          React.createElement("span", { style: { fontSize: 12, color: "var(--ink-3)" } }, "yillik")
        ),
        React.createElement("div", { style: { fontSize: 12, color: "var(--ink-3)", marginBottom: 14 } }, "Maqsad: 22% · o'tgan chorak: 17.3%"),
        React.createElement(SvgAreaChart, { data: profitData, width: 520, height: 120, color: "oklch(0.78 0.14 95)" }),
        React.createElement("div", { style: { display: "flex", justifyContent: "space-between", fontSize: 10, fontFamily: "var(--font-mono)", color: "var(--ink-3)", marginTop: 8 } },
          React.createElement("span", null, "Yan"),
          React.createElement("span", null, "Mar"),
          React.createElement("span", null, "Iyun"),
          React.createElement("span", null, "Sen"),
          React.createElement("span", null, "Dek")
        )
      ),
      // Regional heatmap
      React.createElement(
        "div",
        { style: { border: "1px solid var(--rule)", background: "var(--paper-2)", borderRadius: 14, padding: "22px 24px 24px" } },
        React.createElement(
          "div",
          { style: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 } },
          React.createElement("div", { style: { fontFamily: "var(--font-display)", fontSize: 20 } }, "Hududlar kesimida"),
          React.createElement("span", { style: { fontSize: 11, fontFamily: "var(--font-mono)", padding: "4px 10px", borderRadius: 999, background: "color-mix(in oklab, var(--accent) 12%, var(--paper-3))", color: "var(--accent)" } }, "O'rtacha: 74.2")
        ),
        React.createElement(
          "div",
          { style: { display: "grid", gap: 10 } },
          ...[
            { name: "Andijon vil.", score: 81.5, q: 342 },
            { name: "Toshkent vil.", score: 78.2, q: 286 },
            { name: "Farg'ona vil.", score: 76.4, q: 264 },
            { name: "Samarqand vil.", score: 73.8, q: 231 },
            { name: "Buxoro vil.", score: 71.0, q: 198 },
            { name: "Sirdaryo vil.", score: 64.3, q: 156 },
            { name: "Qashqadaryo vil.", score: 62.1, q: 142 },
            { name: "Surxondaryo vil.", score: 58.7, q: 128 },
          ].map((r, i) =>
            React.createElement(
              "div",
              { key: i, style: { display: "grid", gridTemplateColumns: "110px 1fr auto auto", gap: 10, alignItems: "center" } },
              React.createElement("span", { style: { fontSize: 12, fontWeight: 500 } }, r.name),
              React.createElement(
                "div",
                { style: { height: 6, borderRadius: 999, background: "var(--paper-3)", overflow: "hidden" } },
                React.createElement("div", { style: { height: "100%", borderRadius: 999, background: r.score >= 70 ? "var(--accent)" : r.score >= 55 ? "oklch(0.7 0.16 60)" : "oklch(0.55 0.18 25)", width: `${r.score}%`, opacity: 0.5 + (i / 8) * 0.5 } })
              ),
              React.createElement("span", { style: { fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--ink-3)", width: 40, textAlign: "right" } }, r.q + " ta"),
              React.createElement("span", { style: { fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--ink)", fontWeight: 500, width: 36, textAlign: "right" } }, r.score)
            )
          )
        )
      )
    )
  );
}

function ReportsSection() {
  const [activeReport, setActiveReport] = React.useState(0);
  const reports = [
    { name: "Choraklik KPI hisobot", desc: "Barcha ijarachilarning choraklik KPI natijalari, toifalari va dinamikasi." },
    { name: "Soliq-statistika tafovut", desc: "Soliq va statistika hisobotlari o'rtasidagi farqlar tahlili." },
    { name: "Suv sarfi tahlili", desc: "Hududlar va ijarachilar kesimida suv sarfi dinamikasi." },
    { name: "Hosildorlik dinamikasi", desc: "Ekin turlari bo'yicha hosildorlik o'zgarishi." },
    { name: "Reyting o'zgarishi", desc: "Oldingi chorakka nisbatan reyting o'sishi yoki pasayishi." },
  ];
  const barData = [
    { label: "Yan", value: 42 }, { label: "Fev", value: 55 }, { label: "Mar", value: 48 },
    { label: "Apr", value: 62 }, { label: "May", value: 58 }, { label: "Iyun", value: 70 },
    { label: "Iyul", value: 65 }, { label: "Avg", value: 78 }, { label: "Sen", value: 82 },
    { label: "Okt", value: 75 }, { label: "Noy", value: 88 }, { label: "Dek", value: 92 },
  ];
  return React.createElement(
    "section",
    { id: "hisobotlar", style: styles.section },
    React.createElement("div", { style: styles.sectionEyebrow }, "09 · Hisobotlar"),
    React.createElement("h2", { style: styles.sectionH2 }, "Har qanday hisobot — bir necha soniyada."),
    React.createElement("p", { style: styles.sectionLede }, "Tayyor shablonlar, avtomatik hisoblash va eksport. Ma'lumotlarni Excel, PDF yoki API orqali oling."),
    React.createElement(
      "div",
      { style: { display: "grid", gridTemplateColumns: "minmax(0, 260px) minmax(0, 1fr)", gap: 0, border: "1px solid var(--rule)", borderRadius: 16, background: "var(--paper-2)", overflow: "hidden" } },
      // Sidebar
      React.createElement(
        "div",
        { style: { borderRight: "1px solid var(--rule)", background: "var(--paper-3)", padding: "16px 14px", display: "grid", gap: 4, alignContent: "start" } },
        React.createElement("div", { style: { fontSize: 11, fontFamily: "var(--font-mono)", color: "var(--ink-3)", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 8, paddingLeft: 8 } }, "Hisobotlar ro'yxati"),
        ...reports.map((r, i) =>
          React.createElement(
            "button",
            {
              key: i,
              onClick: () => setActiveReport(i),
              style: activeReport === i
                ? { padding: "10px 12px", borderRadius: 10, fontSize: 13, fontWeight: 500, color: "var(--ink)", cursor: "pointer", background: "var(--paper)", border: "1px solid var(--rule)", textAlign: "left" }
                : { padding: "10px 12px", borderRadius: 10, fontSize: 13, fontWeight: 500, color: "var(--ink-2)", cursor: "pointer", background: "transparent", border: "1px solid transparent", textAlign: "left" }
            },
            r.name
          )
        ),
        React.createElement("div", { style: { marginTop: 16, padding: "10px 12px", background: "var(--paper)", borderRadius: 10, border: "1px solid var(--rule)" } },
          React.createElement("div", { style: { fontSize: 11, fontFamily: "var(--font-mono)", color: "var(--ink-3)", marginBottom: 6 } }, "Eksport"),
          React.createElement("div", { style: { display: "flex", gap: 6 } },
            React.createElement("span", { style: { padding: "4px 8px", background: "var(--paper-3)", borderRadius: 6, fontSize: 10, fontFamily: "var(--font-mono)", border: "1px solid var(--rule)" } }, "Excel"),
            React.createElement("span", { style: { padding: "4px 8px", background: "var(--paper-3)", borderRadius: 6, fontSize: 10, fontFamily: "var(--font-mono)", border: "1px solid var(--rule)" } }, "PDF"),
            React.createElement("span", { style: { padding: "4px 8px", background: "var(--paper-3)", borderRadius: 6, fontSize: 10, fontFamily: "var(--font-mono)", border: "1px solid var(--rule)" } }, "API")
          )
        )
      ),
      // Preview
      React.createElement(
        "div",
        { style: { padding: "22px 24px 24px", minWidth: 0 } },
        React.createElement("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 12 } },
          React.createElement("div", { style: { fontFamily: "var(--font-display)", fontSize: 20 } }, reports[activeReport].name),
          React.createElement("span", { style: { fontSize: 11, fontFamily: "var(--font-mono)", color: "var(--ink-3)" } }, "2026-yil 1-chorak")
        ),
        React.createElement("div", { style: { fontSize: 13, color: "var(--ink-2)", lineHeight: 1.5, marginBottom: 16 } }, reports[activeReport].desc),
        React.createElement(SvgBarChart, { data: barData, width: 540, height: 140, color: "var(--accent)" }),
        React.createElement("div", { style: { marginTop: 24, fontSize: 13, fontWeight: 500, marginBottom: 10 } }, "Top 10 ijarachi"),
        React.createElement(
          "div",
          { style: { display: "grid", gap: 1, background: "var(--rule)", borderRadius: 10, overflow: "hidden", border: "1px solid var(--rule)" } },
          React.createElement(
            "div",
            { style: { display: "grid", gridTemplateColumns: "30px minmax(0, 1fr) auto auto auto", gap: 12, padding: "8px 12px", background: "var(--paper-3)", fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--ink-3)", letterSpacing: "0.04em", textTransform: "uppercase" } },
            React.createElement("span", null, "#"),
            React.createElement("span", null, "Ijarachi"),
            React.createElement("span", null, "Yo'nalish"),
            React.createElement("span", null, "Hudud"),
            React.createElement("span", { style: { textAlign: "right" } }, "Ball")
          ),
          ...[
            { rank: "01", name: "Andijon Bog'dorchilik MChJ", dir: "Bog'dorchilik", region: "Andijon", score: "92.3" },
            { rank: "02", name: "Olmazor Sabzavotchilik", dir: "Sabzavot", region: "Toshkent", score: "88.7" },
            { rank: "03", name: "Farg'ona Premium Uzum", dir: "Uzumchilik", region: "Farg'ona", score: "84.1" },
            { rank: "04", name: "Samarqand Chorva Klasteri", dir: "Chorvachilik", region: "Samarqand", score: "76.2" },
            { rank: "05", name: "Sirdaryo Aylanma", dir: "Sabzavot", region: "Sirdaryo", score: "64.8" },
            { rank: "06", name: "Buxoro Sabzavot LTD", dir: "Sabzavot", region: "Buxoro", score: "62.5" },
            { rank: "07", name: "Jizzax Polizchilik", dir: "Polizchilik", region: "Jizzax", score: "58.3" },
            { rank: "08", name: "Navoiy Chorva XK", dir: "Chorvachilik", region: "Navoiy", score: "55.1" },
            { rank: "09", name: "Qarshi Bog' MChJ", dir: "Bog'dorchilik", region: "Qashqadaryo", score: "51.8" },
            { rank: "10", name: "Surxondaryo Aylanma", dir: "Sabzavot", region: "Surxondaryo", score: "48.6" },
          ].map((r, i) =>
            React.createElement(
              "div",
              { key: i, style: { display: "grid", gridTemplateColumns: "30px minmax(0, 1fr) auto auto auto", gap: 12, padding: "7px 12px", background: "var(--paper)", alignItems: "center", fontSize: 12, borderBottom: i < 9 ? "1px solid var(--rule)" : "none" } },
              React.createElement("span", { style: { fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--ink-3)" } }, r.rank),
              React.createElement("span", { style: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } }, r.name),
              React.createElement("span", { style: { fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--ink-3)" } }, r.dir),
              React.createElement("span", { style: { fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--ink-3)" } }, r.region),
              React.createElement("span", { style: { fontFamily: "var(--font-mono)", fontSize: 12, fontWeight: 500, textAlign: "right", color: parseFloat(r.score) >= 70 ? "var(--accent)" : parseFloat(r.score) >= 55 ? "oklch(0.7 0.16 60)" : "oklch(0.55 0.18 25)" } }, r.score)
            )
          )
        )
      )
    )
  );
}

function RiskSection() {
  const risks = [
    { name: "Hosildorlik anomaliyasi", level: "high", color: "oklch(0.55 0.18 25)", desc: "Buxoro Sabzavot LTD — hosildorlik o'rtachadan 180% yuqori. Soliq va statistika hisobotlari o'rtasida 22% farq." },
    { name: "Suv sarfi ogohlantirishi", level: "high", color: "oklch(0.55 0.18 25)", desc: "Qarshi Bog' MChJ — suv sarfi o'tgan chorakning 3.1 baravar ko'p. Limit 18% oshiq." },
    { name: "NDVI keskin pasayish", level: "med", color: "oklch(0.7 0.16 60)", desc: "Jizzax Polizchilik — O'zbekkosmos ma'lumotlari bo'yicha mart oyida NDVI indeksi keskin pasaygan." },
    { name: "Hisobot kechikishi", level: "low", color: "oklch(0.65 0.14 95)", desc: "Surxondaryo Aylanma — choraklik hisobot 31 kun kechikkan. Avtomatik eslatma yuborildi." },
  ];
  const levelLabel = { high: "Yuqori", med: "O'rta", low: "Past" };
  const riskMatrix = [
    [1, 1, 2, 2, 3], [1, 2, 2, 3, 3], [2, 2, 3, 3, 4], [2, 3, 3, 4, 4], [3, 3, 4, 4, 5]
  ];
  return React.createElement(
    "section",
    { id: "risk", style: styles.section },
    React.createElement("div", { style: styles.sectionEyebrow }, "10 · Risk tahlili"),
    React.createElement("h2", { style: styles.sectionH2 }, "Xavflarni oldindan ko'ring."),
    React.createElement("p", { style: styles.sectionLede }, "AI yordamida hisobotlar, sun'iy yo'ldosh va sensor ma'lumotlarini tahlil qilish orqali xavf va anomaliyalarni avtomatik aniqlash."),
    React.createElement(
      "div",
      { style: { display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 400px)", gap: "max(24px, 4vw)", alignItems: "start" } },
      // Left
      React.createElement(
        "div",
        { style: { display: "grid", gap: 14 } },
        // Risk matrix
        React.createElement(
          "div",
          { style: { border: "1px solid var(--rule)", background: "var(--paper-2)", borderRadius: 14, padding: "18px 20px 20px" } },
          React.createElement("div", { style: { fontSize: 13, fontWeight: 500, marginBottom: 12 } }, "Xavf matritsasi"),
          React.createElement(
            "div",
            { style: { display: "grid", gridTemplateColumns: "auto repeat(5, 1fr)", gap: 3, alignItems: "center" } },
            React.createElement("div", { style: { fontSize: 9, fontFamily: "var(--font-mono)", color: "var(--ink-3)", textAlign: "right", paddingRight: 6 } }, "Ehtimollik ↓"),
            ...["Juda kam", "Kam", "O'rta", "Yuqori", "Juda yuqori"].map((l, i) =>
              React.createElement("div", { key: i, style: { fontSize: 9, fontFamily: "var(--font-mono)", color: "var(--ink-3)", textAlign: "center" } }, l)
            ),
            ...riskMatrix.flatMap((row, ri) => [
              React.createElement("div", { key: `l${ri}`, style: { fontSize: 9, fontFamily: "var(--font-mono)", color: "var(--ink-3)", textAlign: "right", paddingRight: 6 } }, ["Juda kichik", "Kichik", "O'rta", "Katta", "Juda katta"][ri]),
              ...row.map((cell, ci) => {
                const colors = { 1: "oklch(0.62 0.16 152)", 2: "oklch(0.78 0.14 95)", 3: "oklch(0.7 0.16 60)", 4: "oklch(0.66 0.16 35)", 5: "oklch(0.55 0.18 25)" };
                const isActive = (ri === 3 && ci === 2) || (ri === 4 && ci === 1) || (ri === 2 && ci === 3);
                return React.createElement("div", { key: `${ri}-${ci}`, style: { aspectRatio: "1", borderRadius: 6, background: colors[cell], opacity: isActive ? 1 : 0.25, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontFamily: "var(--font-mono)", color: "white", fontWeight: 600 } }, cell);
              })
            ])
          )
        ),
        // Risk list
        ...risks.map((r, i) =>
          React.createElement(
            "div",
            { key: i, style: { border: "1px solid var(--rule)", background: "var(--paper-2)", borderRadius: 12, padding: "14px 16px" } },
            React.createElement(
              "div",
              { style: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 4 } },
              React.createElement("span", { style: { fontSize: 13, fontWeight: 500 } }, r.name),
              React.createElement("span", { style: { fontSize: 10, fontFamily: "var(--font-mono)", padding: "3px 8px", borderRadius: 999, letterSpacing: "0.04em", textTransform: "uppercase", background: "color-mix(in oklab, " + r.color + " 12%, var(--paper-3))", color: r.color } }, levelLabel[r.level])
            ),
            React.createElement("div", { style: { fontSize: 12, color: "var(--ink-2)", lineHeight: 1.5 } }, r.desc)
          )
        )
      ),
      // Right - AI card
      React.createElement(
        "div",
        { style: { border: "1px solid var(--rule)", background: "var(--paper-2)", borderRadius: 14, padding: "22px 22px 24px", position: "sticky", top: 80 } },
        React.createElement("div", { style: { display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, fontFamily: "var(--font-mono)", color: "var(--accent)", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 12 } },
          React.createElement("span", null, "◉"),
          React.createElement("span", null, "AI tahlil")
        ),
        React.createElement("div", { style: { fontFamily: "var(--font-display)", fontSize: 22, marginBottom: 8 } }, "Buxoro Sabzavot LTD"),
        React.createElement("div", { style: { fontSize: 12, color: "var(--ink-3)", marginBottom: 14 } }, "ID: #1847 · Buxoro vil. · Sabzavot · 142 ga"),
        React.createElement("div", { style: { fontSize: 13, color: "var(--ink-2)", lineHeight: 1.6, marginBottom: 14 } }, "AI model hosildorlik va soliq hisobotlari o'rtasidagi 22% farqni aniqladi. Bu yer ijarachisining hosildorlik ko'rsatkichlarini oshirish uchun soliq hisobotlarini noto'g'ri to'ldirish ehtimolini ko'rsatadi."),
        React.createElement(
          "div",
          { style: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 14 } },
          React.createElement("div", { style: { padding: "10px", background: "var(--paper-3)", borderRadius: 8, border: "1px solid var(--rule)" } },
            React.createElement("div", { style: { fontSize: 10, fontFamily: "var(--font-mono)", color: "var(--ink-3)", marginBottom: 2 } }, "Xavf darajasi"),
            React.createElement("div", { style: { fontFamily: "var(--font-display)", fontSize: 18, color: "oklch(0.55 0.18 25)" } }, "Yuqori")
          ),
          React.createElement("div", { style: { padding: "10px", background: "var(--paper-3)", borderRadius: 8, border: "1px solid var(--rule)" } },
            React.createElement("div", { style: { fontSize: 10, fontFamily: "var(--font-mono)", color: "var(--ink-3)", marginBottom: 2 } }, "Model ishonchi"),
            React.createElement("div", { style: { fontFamily: "var(--font-display)", fontSize: 18, color: "var(--accent)" } }, "94.2%")
          )
        ),
        React.createElement(
          "div",
          { style: { background: "var(--paper-3)", border: "1px solid var(--rule)", borderRadius: 10, padding: "12px 14px", fontSize: 13, color: "var(--ink-2)", lineHeight: 1.5 } },
          React.createElement("strong", null, "Tavsiya: "),
          "Viloyat inspeksiyasi tomonidan tekshiruv o'tkazish, soliq va statistika organlari ma'lumotlarini qayta tekshirish."
        )
      )
    )
  );
}

function AdminSection() {
  const [tab, setTab] = React.useState("admin");
  const tabs = [
    { id: "admin", label: "Rollar" },
    { id: "bildirishnoma", label: "Bildirishnomalar" },
    { id: "audit", label: "Audit" },
  ];
  const roles = [
    { role: "Administrator", perm: "Tizim sozlamalari, foydalanuvchilar, integratsiyalar", count: "3" },
    { role: "Operator", perm: "Hisobotlar, monitoring, baholash, eksport", count: "24" },
    { role: "Tahlilchi", perm: "Analitika, risk tahlili, AI modullar, eksport", count: "8" },
    { role: "Kuzatuvchi", perm: "Faqat ko'rish, hisobot olish", count: "12" },
  ];
  const notifs = [
    { emoji: "⚠️", title: "Suv limiti oshiq — Sirdaryo Aylanma", meta: "5 daqiqa oldin", color: "oklch(0.55 0.18 25)" },
    { emoji: "📊", title: "Choraklik hisobot tayyor — 1,847 ta", meta: "2 soat oldin", color: "var(--accent)" },
    { emoji: "🔔", title: "Hisobot kechikdi — Surxondaryo Aylanma", meta: "1 kun oldin", color: "oklch(0.7 0.16 60)" },
  ];
  const audits = [
    { time: "14:32", action: "KPI baholash o'tkazildi (1,847 ta)", user: "A. Karimov", role: "Operator" },
    { time: "14:15", action: "Yangi ijarachi qo'shildi (#1848)", user: "S. Rahimov", role: "Operator" },
    { time: "13:58", action: "Hisobot eksport qilindi (Excel)", user: "N. Ismoilov", role: "Tahlilchi" },
    { time: "13:42", action: "Risk tahlili ishga tushirildi", user: "A. Karimov", role: "Operator" },
    { time: "13:20", action: "Sozlamalar o'zgartirildi", user: "Administrator", role: "Admin" },
    { time: "12:45", action: "Foydalanuvchi huquqlari yangilandi", user: "Administrator", role: "Admin" },
    { time: "12:10", action: "API kaliti yangilandi", user: "Administrator", role: "Admin" },
  ];
  return React.createElement(
    "section",
    { id: "admin", style: styles.section },
    React.createElement("div", { style: styles.sectionEyebrow }, "11 · Boshqaruv"),
    React.createElement("h2", { style: styles.sectionH2 }, "Tizim to'liq nazoratingizda."),
    React.createElement("p", { style: styles.sectionLede }, "Rollar, huquqlar, bildirishnomalar va har bir foydalanuvchi harakati — barchasi kuzatuv ostida."),
    React.createElement(
      "div",
      { style: { border: "1px solid var(--rule)", background: "var(--paper-2)", borderRadius: 16, overflow: "hidden" } },
      React.createElement(
        "div",
        { style: { display: "flex", gap: 0, borderBottom: "1px solid var(--rule)", background: "var(--paper-3)", padding: "0 14px" } },
        ...tabs.map((t) =>
          React.createElement(
            "button",
            {
              key: t.id,
              onClick: () => setTab(t.id),
              style: tab === t.id
                ? { padding: "12px 16px", fontSize: 13, fontWeight: 500, color: "var(--ink)", borderBottom: "2px solid var(--accent)", cursor: "pointer", background: "transparent", border: "none", borderBottomColor: "var(--accent)" }
                : { padding: "12px 16px", fontSize: 13, fontWeight: 500, color: "var(--ink-3)", borderBottom: "2px solid transparent", cursor: "pointer", background: "transparent", border: "none" }
            },
            t.label
          )
        )
      ),
      React.createElement("div", { style: { padding: "22px 24px 24px" } },
        tab === "admin" && React.createElement(
          React.Fragment,
          null,
          React.createElement("div", { style: { fontSize: 13, fontWeight: 500, marginBottom: 12 } }, "Foydalanuvchi rollari va huquqlari"),
          React.createElement(
            "div",
            { style: { display: "grid", gap: 1, background: "var(--rule)", borderRadius: 10, overflow: "hidden", border: "1px solid var(--rule)" } },
            React.createElement(
              "div",
              { style: { display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.5fr) auto", gap: 12, padding: "9px 12px", background: "var(--paper-3)", fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--ink-3)", letterSpacing: "0.04em", textTransform: "uppercase" } },
              React.createElement("span", null, "Rol"),
              React.createElement("span", null, "Huquqlar"),
              React.createElement("span", { style: { textAlign: "right" } }, "Soni")
            ),
            ...roles.map((r, i) =>
              React.createElement(
                "div",
                { key: i, style: { display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.5fr) auto", gap: 12, padding: "9px 12px", background: "var(--paper)", alignItems: "center", fontSize: 13, borderBottom: i < 3 ? "1px solid var(--rule)" : "none" } },
                React.createElement("span", { style: { fontWeight: 500 } }, r.role),
                React.createElement("span", { style: { fontSize: 12, color: "var(--ink-2)" } }, r.perm),
                React.createElement("span", { style: { fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--ink-3)", textAlign: "right" } }, r.count)
              )
            )
          )
        ),
        tab === "bildirishnoma" && React.createElement(
          React.Fragment,
          null,
          React.createElement("div", { style: { fontSize: 13, fontWeight: 500, marginBottom: 12 } }, "So'nggi bildirishnomalar"),
          ...notifs.map((n, i) =>
            React.createElement(
              "div",
              { key: i, style: { display: "flex", alignItems: "flex-start", gap: 12, padding: "12px 14px", borderRadius: 10, background: "var(--paper)", border: "1px solid var(--rule)", marginBottom: 8 } },
              React.createElement("span", { style: { width: 8, height: 8, borderRadius: 999, marginTop: 5, flexShrink: 0, background: n.color } }),
              React.createElement(
                "div",
                { style: { minWidth: 0 } },
                React.createElement("div", { style: { fontSize: 13, fontWeight: 500, marginBottom: 2 } }, n.emoji, " ", n.title),
                React.createElement("div", { style: { fontSize: 11, fontFamily: "var(--font-mono)", color: "var(--ink-3)" } }, n.meta)
              )
            )
          )
        ),
        tab === "audit" && React.createElement(
          React.Fragment,
          null,
          React.createElement("div", { style: { fontSize: 13, fontWeight: 500, marginBottom: 12 } }, "Harakatlar tarixi (so'nggi 7 ta)"),
          ...audits.map((a, i) =>
            React.createElement(
              "div",
              { key: i, style: { display: "grid", gridTemplateColumns: "50px minmax(0, 1fr) auto auto", gap: 12, padding: "9px 12px", background: "var(--paper)", alignItems: "center", fontSize: 13, borderBottom: i < 6 ? "1px solid var(--rule)" : "none" } },
              React.createElement("span", { style: { fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--ink-3)" } }, a.time),
              React.createElement("span", null, a.action),
              React.createElement("span", { style: { fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--ink-3)", textAlign: "right" } }, a.user),
              React.createElement("span", { style: { fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--ink-3)", textAlign: "right", padding: "2px 6px", background: "var(--paper-3)", borderRadius: 4, border: "1px solid var(--rule)" } }, a.role)
            )
          )
        )
      )
    )
  );
}

function MobileSection() {
  const features = ["📶 Offline rejim", "📍 Geolokatsiya", "🔔 Push bildirishnomalar", "📎 Hujjat yuklash", "🔐 Biometrik kirish"];
  return React.createElement(
    "section",
    { id: "mobil", style: styles.section },
    React.createElement("div", { style: styles.sectionEyebrow }, "12 · Mobil ilova"),
    React.createElement("h2", { style: styles.sectionH2 }, "Har vaqt, har joyda."),
    React.createElement("p", { style: styles.sectionLede }, "iOS va Android uchun mobil ilova. Offline rejimda ma'lumotlarni ko'ring, geolokatsiya orqali yer maydonini tekshiring, hujjat va rasmlarni yuklang."),
    React.createElement(
      "div",
      { style: { display: "grid", gridTemplateColumns: "minmax(0, 280px) minmax(0, 1fr)", gap: "max(32px, 5vw)", alignItems: "center" } },
      React.createElement(
        "div",
        { style: { width: 260, height: 520, borderRadius: 36, border: "3px solid var(--ink)", background: "var(--ink)", padding: 10, boxShadow: "0 30px 80px -30px rgba(0,0,0,0.35)", margin: "0 auto" } },
        React.createElement(
          "div",
          { style: { width: "100%", height: "100%", borderRadius: 26, background: "var(--paper-2)", overflow: "hidden", border: "1px solid var(--rule)" } },
          React.createElement("div", { style: { width: 80, height: 20, borderRadius: "0 0 12px 12px", background: "var(--ink)", margin: "0 auto" } }),
          React.createElement(
            "div",
            { style: { padding: "14px 16px 16px" } },
            React.createElement("div", { style: { fontSize: 11, fontFamily: "var(--font-mono)", color: "var(--ink-3)", marginBottom: 8 } }, "YER IJARACHISI"),
            React.createElement("div", { style: { fontFamily: "var(--font-display)", fontSize: 18, marginBottom: 12 } }, "Andijon Bog'dorchilik"),
            React.createElement("div", { style: { fontFamily: "var(--font-display)", fontSize: 42, color: "var(--accent)", lineHeight: 1, marginBottom: 4 } }, "83.8"),
            React.createElement("div", { style: { fontSize: 11, fontFamily: "var(--font-mono)", color: "var(--ink-3)", marginBottom: 14 } }, "/100 · O'rta toifa"),
            React.createElement(
              "div",
              { style: { display: "grid", gap: 8 } },
              React.createElement("div", { style: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 12px", background: "var(--paper-3)", borderRadius: 10, fontSize: 13 } },
                React.createElement("span", null, "Iqtisodiy"),
                React.createElement("span", { style: { fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--accent)" } }, "31.5")
              ),
              React.createElement("div", { style: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 12px", background: "var(--paper-3)", borderRadius: 10, fontSize: 13 } },
                React.createElement("span", null, "Agrobiologik"),
                React.createElement("span", { style: { fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--accent)" } }, "24.0")
              ),
              React.createElement("div", { style: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 12px", background: "var(--paper-3)", borderRadius: 10, fontSize: 13 } },
                React.createElement("span", null, "Resurslar"),
                React.createElement("span", { style: { fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--accent)" } }, "16.6")
              ),
              React.createElement("div", { style: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 12px", background: "var(--paper-3)", borderRadius: 10, fontSize: 13 } },
                React.createElement("span", null, "Moliyaviy"),
                React.createElement("span", { style: { fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--accent)" } }, "11.7")
              )
            ),
            React.createElement(
              "div",
              { style: { marginTop: 12, padding: "8px", background: "var(--paper-3)", borderRadius: 10, border: "1px solid var(--rule)" } },
              React.createElement("div", { style: { fontSize: 10, fontFamily: "var(--font-mono)", color: "var(--ink-3)", marginBottom: 4 } }, "So'nggi ogohlantirish"),
              React.createElement("div", { style: { fontSize: 11, color: "var(--ink-2)", lineHeight: 1.4 } }, "⚠️ Suv limiti 18% oshiq")
            )
          )
        )
      ),
      React.createElement(
        "div",
        null,
        React.createElement("div", { style: { fontSize: 17, fontWeight: 500, marginBottom: 10 } }, "Mobil ilova imkoniyatlari"),
        React.createElement(
          "div",
          { style: { display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12 } },
          ...features.map((f, i) =>
            React.createElement("span", { key: i, style: { padding: "8px 14px", borderRadius: 999, background: "var(--paper-2)", border: "1px solid var(--rule)", fontSize: 13, fontWeight: 500, color: "var(--ink-2)", display: "inline-flex", alignItems: "center", gap: 6 } }, f)
          )
        ),
        React.createElement(
          "div",
          { style: { marginTop: 28, display: "grid", gap: 14 } },
          React.createElement("div", { style: { fontSize: 13, color: "var(--ink-2)", lineHeight: 1.6 } }, React.createElement("strong", null, "Offline rejim."), " Internet bo'lmasa ham ma'lumotlarni ko'ring. Keyin sinxronlash."),
          React.createElement("div", { style: { fontSize: 13, color: "var(--ink-2)", lineHeight: 1.6 } }, React.createElement("strong", null, "Geolokatsiya."), " Yer maydonini GPS orqali tekshiring, chegaralarni solishtiring."),
          React.createElement("div", { style: { fontSize: 13, color: "var(--ink-2)", lineHeight: 1.6 } }, React.createElement("strong", null, "Hujjat yuklash."), " Foto, dalolatnoma, shartnoma va boshqa hujjatlarni to'g'ridan-to'g'ri yuklang."),
          React.createElement("div", { style: { fontSize: 13, color: "var(--ink-2)", lineHeight: 1.6 } }, React.createElement("strong", null, "Biometrik kirish."), " Face ID / Touch ID orqali xavfsiz kirish.")
        )
      )
    )
  );
}

function AiSection() {
  const pipeline = [
    { stage: "Ma'lumot manbalari", items: ["Soliq", "Statistika", "Suv", "O'zbekkosmos", "Veterinariya", "Kadastr"] },
    { stage: "Tozalash va sinxronlash", items: ["Validatsiya", "Deduplikatsiya", "Normalizatsiya", "Sinxronlash"] },
    { stage: "AI model", items: ["Risk Scoring", "Anomaliya", "NDVI tahlili", "Chatbot", "Tavsiyalar"] },
    { stage: "Natija", items: ["Dashboard", "Hisobotlar", "Ogohlantirishlar", "API"] },
  ];
  const aiCards = [
    { emoji: "🛡️", name: "AI Risk Scoring", desc: "Yer ijarachilarida xavf darajasini avtomatik aniqlash. Hisobotlar, sun'iy yo'ldosh va sensor ma'lumotlarini tahlil qiladi." },
    { emoji: "🛰️", name: "Sun'iy yo'ldosh tahlili", desc: "O'zbekkosmos NDVI ma'lumotlari asosida yer foydalanish holatini AI orqali baholaydi. Ekin sifati va maydon holati." },
    { emoji: "💧", name: "Suv-hosildorlik nomuvofiqligi", desc: "Suv sarfi va hosildorlik o'rtasidagi logik bog'liqlikni buzish holatlarini aniqlaydi." },
    { emoji: "📑", name: "Soliq-statistika tafovutlari", desc: "Soliq va statistika hisobotlari o'rtasidagi tabiiy farqdan katta tafovutlarni avtomatik belgilaydi." },
    { emoji: "💬", name: "AI Chatbot", desc: "Operator va ijarachilar uchun yordamchi. Savollarga javob, hisobotlar bo'yicha maslahat va tavsiyalar." },
    { emoji: "⚡", name: "Avtomatik tavsiyalar", desc: "KPI natijalariga qarab shaxsiylashtirilgan tavsiyalar generatsiyasi. Nima qilish kerakligini AI aytadi." },
  ];
  return React.createElement(
    "section",
    { id: "ai", style: styles.section },
    React.createElement("div", { style: styles.sectionEyebrow }, "13 · Sun'iy intellekt"),
    React.createElement("h2", { style: styles.sectionH2 }, "AI — nazoratning yangi bosqichi."),
    React.createElement("p", { style: styles.sectionLede }, "Sun'iy intellekt modullari katta ma'lumotlar to'plamini tahlil qilib, inson ko'zi bilan ko'rinmas xavf va imkoniyatlarni aniqlaydi."),
    // Pipeline
    React.createElement(
      "div",
      { style: { border: "1px solid var(--rule)", background: "var(--paper-2)", borderRadius: 14, padding: "20px 24px 24px", marginBottom: 24 } },
      React.createElement("div", { style: { fontSize: 13, fontWeight: 500, marginBottom: 16 } }, "Ma'lumotlar qayta ishlanish pipeline'i"),
      React.createElement(
        "div",
        { style: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, alignItems: "stretch" } },
        ...pipeline.map((p, i) =>
          React.createElement(
            "div",
            { key: i, style: { display: "flex", flexDirection: "column", gap: 8 } },
            React.createElement("div", { style: { fontSize: 11, fontFamily: "var(--font-mono)", color: "var(--accent)", letterSpacing: "0.06em", textTransform: "uppercase" } }, `0${i + 1}. ${p.stage}`),
            React.createElement(
              "div",
              { style: { display: "grid", gap: 4 } },
              ...p.items.map((item, j) =>
                React.createElement("div", { key: j, style: { padding: "6px 10px", background: "var(--paper)", borderRadius: 6, border: "1px solid var(--rule)", fontSize: 12, color: "var(--ink-2)" } }, item)
              )
            ),
            i < 3 && React.createElement("div", { style: { textAlign: "center", fontSize: 18, color: "var(--ink-3)", marginTop: 4 } }, "→")
          )
        )
      )
    ),
    // AI cards
    React.createElement(
      "div",
      { style: { display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 18 } },
      ...aiCards.map((c, i) =>
        React.createElement(
          "div",
          { key: i, style: { border: "1px solid var(--rule)", background: "var(--paper-2)", borderRadius: 14, padding: "22px 22px 24px", minWidth: 0, cursor: "default" } },
          React.createElement("div", { style: { fontSize: 28, marginBottom: 14, lineHeight: 1 } }, c.emoji),
          React.createElement("div", { style: { fontFamily: "var(--font-display)", fontSize: 20, marginBottom: 8, letterSpacing: "-0.01em" } }, c.name),
          React.createElement("div", { style: { fontSize: 13.5, color: "var(--ink-2)", lineHeight: 1.55, marginBottom: 14 } }, c.desc),
          React.createElement("span", { style: { display: "inline-flex", padding: "4px 10px", borderRadius: 999, background: "color-mix(in oklab, var(--accent) 12%, var(--paper-3))", color: "var(--accent)", fontSize: 11, fontFamily: "var(--font-mono)", fontWeight: 500, letterSpacing: "0.04em" } }, "AI modul")
        )
      )
    )
  );
}
function App() {
  return React.createElement(
    "div",
    { style: styles.page },
    React.createElement(Nav, null),
    React.createElement(Hero, null),
    React.createElement(KpiSection, null),
    React.createElement(RankSection, null),
    React.createElement(CategoriesSection, null),
    React.createElement(PlatformSection, null),
    React.createElement(MonitoringSection, null),
    React.createElement(MezonSection, null),
    React.createElement(IntegrationsSection, null),
    React.createElement(FlowSection, null),
    React.createElement(MisolSection, null),
    React.createElement(ReportsSection, null),
    React.createElement(RiskSection, null),
    React.createElement(WizardSection, null),
    React.createElement(OutcomesSection, null),
    React.createElement(AdminSection, null),
    React.createElement(MobileSection, null),
    React.createElement(AiSection, null),
    React.createElement(RoadmapSection, null),
    React.createElement(FaqSection, null),
    React.createElement(Footer, null)
  );
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(React.createElement(App, null));
